  import { useEffect, useState, useRef } from "react";
  import { Badge } from "./ui/badge";
  import { Timer } from "lucide-react";
  import { toast } from "sonner"; 
  import { Practice } from "@/domain/entities/Practice";
  import { bluetoothHelper } from "@/utils/bluetoothHelper";
  import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
  import { Button } from "./ui/button";
  import { PracticeRepository } from "../infrastructure/repositories/PracticeRepository";
  import { FolderRepository } from "../infrastructure/repositories/FolderRepository";
  import { SetList } from "./sets/SetList";
  import { PracticeClockDialog } from "./sets/PracticeClockDialog";
  import { DeleteSetDialog } from "./sets/DeleteSetDialog";
  import { CreatePracticeDialog } from "./sets/CreatePracticeDialog";
  import { PostRepository } from "@/infrastructure/repositories/PostRepository";
  import { ChatRepository } from "@/infrastructure/repositories/ChatRepository";
  import { AddPostDialog } from "./ui/AddPostDialog";
import { Plus, TrendingUp, Heart, Share2, Save, Pencil, Trash2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggleGroup";
  import { secureStorage } from "@/utils/secureStorage";


interface SetItem {
    id: string;
    number: number;
    reps: number;
    minutes: number;
    seconds: number;
    distance: number | "";
    energy_zone: string;
  description?: string;
  }

interface SplitDraftItem {
  id: string;
  reps: string;
  minutes: string;
  seconds: string;
  distance: string;
  energy_zone: string;
  description: string;
}

function createDefaultSplitDraft(preferredEnergyZone: string | null): SplitDraftItem {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    reps: "1",
    minutes: "0",
    seconds: "30",
    distance: "25",
    energy_zone: preferredEnergyZone || "Warm Up",
    description: "",
  };
}
  interface PracticeLap {
  id: string | number;
  practice_id: string | number;
  rep: string | number;
  distance: string | number;
  interval_lap: string | number;
  energy_zone?: string;
  description?: string;
  createdAt?: string;
}


  interface SetsContentProps {
    laps?: any[];
    practiceId?: number | string;
    folderId?: number | string;
    name?: string;
    description?: string;
    practiceDate?: string;
    practiceTime?: string;
  courseTypeLookupId?: number | string;
      onClose?: () => void;
      onSectionChange?: (
    section: string,
    payload?: { sets?: any[]; practiceId?: number | string; name?: string , description?: string;}
  ) => void;
        sets?: any[];
    setSets?: React.Dispatch<React.SetStateAction<any[]>>;
    showManageActions?: boolean;
  }

export function SetsContent({ 
  laps = [], 
  practiceId, 
  folderId,
  name, 
  description, 
  practiceDate,
  practiceTime,
  courseTypeLookupId,
  onClose,
  onSectionChange,
  showManageActions = false
}: SetsContentProps) {
    const [sets, setSets] = useState<SetItem[]>([]);
    const [noLaps, setNoLaps] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [setToDelete, setSetToDelete] = useState<SetItem | null>(null);
    const [loadingAction, setLoadingAction] = useState(false);
    const preferredEnergyZone = localStorage.getItem("preferred_energy_zone") || null; 
    const [isAddSetDialogOpen, setIsAddSetDialogOpen] = useState(false);

    const [singleRounds, setSingleRounds] = useState("1");
    const [splitSetDrafts, setSplitSetDrafts] = useState<SplitDraftItem[]>([
      createDefaultSplitDraft(preferredEnergyZone),
    ]);
    const [isEmptySetsDialogOpen, setIsEmptySetsDialogOpen] = useState(false);
    const [singleFormError, setSingleFormError] = useState<string | null>(null);
    const [ladderFormError, setLadderFormError] = useState<string | null>(null);
    const [localPracticeId, setLocalPracticeId] = useState<number | string | undefined>(practiceId);
    const [isOpenPracticeDialogOpen, setIsOpenPracticeDialogOpen] = useState(false);
  const [allPractices, setAllPractices] = useState<Practice[]>([]);
  const [loadingPractices, setLoadingPractices] = useState(false);
  const [allPracticeLaps, setAllPracticeLaps] = useState<PracticeLap[]>([]);
  const [favoriteFolders, setFavoriteFolders] = useState<any[]>([]);
  const [activeFavoriteFolderId, setActiveFavoriteFolderId] = useState<string | null>(null);
  const [favoriteFolderPractices, setFavoriteFolderPractices] = useState<any[]>([]);
  const [loadingFavoriteFolderPractices, setLoadingFavoriteFolderPractices] = useState(false);
    const [isDirtySets, setIsDirtySets] = useState(false);
    const setsLoadedRef = useRef(false);
    const isManuallyModifiedRef = useRef(false);
    const addDefaultSetLockRef = useRef(false);
    const [practiceImageFile, setPracticeImageFile] = useState<File | null>(null);
    const [practiceImagePreview, setPracticeImagePreview] = useState<string | null>(null);
    const [practiceImageUrl, setPracticeImageUrl] = useState<string | null>(null);
    const [aiImageFile, setAiImageFile] = useState<File | null>(null);
    const [aiImagePreview, setAiImagePreview] = useState<string | null>(null);
    const [aiErrorDialogOpen, setAiErrorDialogOpen] = useState(false);
    const [aiErrorMessage, setAiErrorMessage] = useState("");
    const handleNewPracticeDialogOpenChange = (open: boolean) => {
      setIsNewPracticeDialogOpen(open);
      if (!open) {
        setPracticeImageFile(null);
      }
    };
    const handlePracticeImageChange = async (file: File | null) => {
      if (!file) {
        setPracticeImageFile(null);
        return;
      }

      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      try {
        setLoadingAction(true);
        const repo = new PracticeRepository();
        const uploadResp = await repo.uploadPracticeImage(file);
        if (uploadResp.status !== 1 || !uploadResp.image_link) {
          throw new Error(uploadResp.msg || "Failed to upload practice image");
        }

        const normalizedUrl = uploadResp.image_link.replace(/\\\//g, "/");
        setPracticeImageUrl(normalizedUrl);
        setPracticeImageFile(null);

        const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
        const targetPracticeId = localPracticeId ?? practiceId;
        if (!targetPracticeId) {
          localStorage.setItem(
            "selected_practice",
            JSON.stringify({
              ...selectedPractice,
              imageData: normalizedUrl,
            })
          );
          toast.success("Practice image uploaded");
          return;
        }

        const resolvedName =
          selectedPractice.name ?? name ?? "Untitled Practice";
        const resolvedDescription =
          selectedPractice.description ?? description ?? "";
        const resolvedFavorite =
          selectedPractice.isFavorite ?? isFavorite ?? false;
        const resolvedFolderId = resolveFolderId();

        await savePracticeAndLaps(
          targetPracticeId,
          resolvedName,
          resolvedDescription,
          resolvedFavorite,
          resolvedFolderId,
          null,
          normalizedUrl
        );

        const refreshed = JSON.parse(localStorage.getItem("selected_practice") || "{}");
        const refreshedId =
          refreshed.practiceId ?? refreshed.practice_id ?? refreshed.id;
        if (refreshedId) {
          setLocalPracticeId(refreshedId);
        }

        toast.success("Practice image uploaded");
      } catch (err) {
        console.error("Failed to upload practice image", err);
        toast.error("Failed to upload practice image");
      } finally {
        setLoadingAction(false);
      }
    };

    const handleAiImageChange = async (file: File | null) => {
      if (!file) {
        setAiImageFile(null);
        return;
      }

      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      const userId =
        localStorage.getItem("selected_swimmer_id") ||
        localStorage.getItem("current_user_swimmer_id") ||
        "";

      if (!userId) {
        toast.error("User not found");
        return;
      }

      const userTypeRaw =
        secureStorage.get<string>("user_type") ||
        localStorage.getItem("user_type") ||
        "swimmer";
      const userType = String(userTypeRaw).toLowerCase() === "coach" ? "coach" : "swimmer";
      const coachIdToSend =
        userType === "coach" ? "" : String(localStorage.getItem("coach_id") || "");
      const isCoachRequest = userType === "coach";

      try {
        setLoadingAction(true);
        setAiImageFile(file);

        const chatRepo = new ChatRepository();
        const { parsedAI } = await chatRepo.sendMessageToAI(
          "Generate practice from image",
          String(userId),
          coachIdToSend,
          isCoachRequest,
          file
        );

        const ai: any = parsedAI ?? null;
        if (ai?.status === 0 || ai?.msg === "Parse error") {
          const raw = String(ai?.original_response ?? ai?.msg ?? "Unable to parse AI response.");
          const extracted =
            raw.match(/msg['"]?\s*:\s*['"]([^'"]+)['"]/i)?.[1] ??
            raw;
          setAiErrorMessage(extracted);
          setAiErrorDialogOpen(true);
          return;
        }
        const practices = ai?.data?.practices ?? ai?.data?.practice ?? [];
        const laps = ai?.data?.laps ?? ai?.data?.practice_laps ?? [];
        const practiceFromAi = Array.isArray(practices) && practices.length > 0 ? practices[0] : null;
        const aiLaps =
          Array.isArray(laps) && laps.length > 0
            ? laps
            : Array.isArray(practiceFromAi?.laps)
            ? practiceFromAi.laps
            : [];



        if (!practiceFromAi || aiLaps.length === 0) {
          toast.error("No practice data found in AI response");
          return;
        }

        const newSets = aiLaps.map((lap: any, index: number) => {
          const intervalSeconds = parseInt(String(lap.interval ?? lap.interval_lap ?? 0)) || 0;
          const parsedDistance = parseInt(String(lap.distance));
          return {
            id: crypto.randomUUID(),
            number: index + 1,
            reps: parseInt(String(lap.rep)) || 1,
            minutes: Math.floor(intervalSeconds / 60),
            seconds: intervalSeconds % 60,
            distance:
              !isNaN(parsedDistance) && lap.distance != null && lap.distance !== ""
                ? parsedDistance
                : 25,
            energy_zone: lap.energy_zone || "Warm Up",
            description: lap.description || `Lap ${index + 1}`,
          };
        });

        setSets(normalizeSets(newSets));
        isManuallyModifiedRef.current = true;
        setIsDirtySets(true);

        if (practiceFromAi?.name) setNewPracticeName(String(practiceFromAi.name));
        if (practiceFromAi?.description) setNewPracticeDescription(String(practiceFromAi.description));

        toast.success("AI practice loaded");
      } catch (err) {
        console.error("Failed to process AI image", err);
        toast.error("Failed to process AI image");
      } finally {
        setLoadingAction(false);
      }
    };

useEffect(() => {
  if (!practiceImageFile) {
    setPracticeImagePreview(null);
    return;
  }

  const url = URL.createObjectURL(practiceImageFile);
  setPracticeImagePreview(url);

  return () => URL.revokeObjectURL(url);
}, [practiceImageFile]);

useEffect(() => {
  if (!aiImageFile) {
    setAiImagePreview(null);
    return;
  }

  const url = URL.createObjectURL(aiImageFile);
  setAiImagePreview(url);

  return () => URL.revokeObjectURL(url);
}, [aiImageFile]);

useEffect(() => {
  // If sets have been manually modified, don't reload from props/laps
  if (isManuallyModifiedRef.current) return;
  
  // Check if this is a draft practice
  const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
  if (selectedPractice.isDraft && selectedPractice.sets && selectedPractice.sets.length > 0) {
    // Only load if we haven't loaded yet
    if (!setsLoadedRef.current) {
      const formatted = selectedPractice.sets.map((set: any, index: number) => ({
        id: set.id || `${Date.now()}-${index}`,
        number: index + 1,
        reps: set.reps || 1,
        minutes: set.minutes || 0,
        seconds: set.seconds || 0,
        distance: (set.distance !== undefined && set.distance !== null) ? set.distance : 25,
        energy_zone: set.energy_zone || "Warm Up",
        description: set.description || `Lap ${index + 1}`,
      }));
      setSets(normalizeSets(formatted));
      setIsDirtySets(true);
      setsLoadedRef.current = true;
    }
    return;
  }

  if (isDirtySets || setsLoadedRef.current) return;

  if (laps && laps.length > 0) {
    const sorted = [...laps].sort((a, b) => {
      if (a.createdAt && b.createdAt) {
        const diff =
          new Date(a.createdAt).getTime() -
          new Date(b.createdAt).getTime();
        if (diff !== 0) return diff;
      }
      return Number(a.id) - Number(b.id);
    });

    const formatted = sorted.map((lap, index) => {
      const parsedDistance = parseInt(String(lap.distance));
      return {
        id: lap.id?.toString() || `${Date.now()}-${index}`,
        number: index + 1,
        reps: parseInt(lap.rep) || 1,
        minutes: Math.floor(
          (parseInt(String(lap.interval_lap ?? (lap as any).interval ?? 0)) || 0) / 60
        ),
        seconds:
          (parseInt(String(lap.interval_lap ?? (lap as any).interval ?? 0)) || 0) % 60,
        distance: (!isNaN(parsedDistance) && lap.distance != null && lap.distance !== '') ? parsedDistance : 25,
        energy_zone: lap.energy_zone || "Warm Up",
        description: lap.description || `Lap ${index + 1}`,
      };
    });

    setSets(normalizeSets(formatted));
    setsLoadedRef.current = true;
  }
}, [laps, isDirtySets]);


useEffect(() => {
  setLocalPracticeId(practiceId);
  // Reset refs when practice changes
  setsLoadedRef.current = false;
  isManuallyModifiedRef.current = false;
}, [practiceId]);

useEffect(() => {
  setNoLaps(sets.length === 0);
}, [sets]);

  const energyZones = [
    "Warm Up",
    "En1 Moderate",
    "En3 Threshold",
    "Sp1 Best Average",
    "Sp2 Race Pace",
    "Sp3 Sprint",
    "Recovery",
    "Extra Rest",
    "Aerobic",
    "Endurance",
    "En2 Threshold"
  ];

  const normalizeSets = (list: SetItem[]) => {
    return list
      .sort((a, b) => a.number - b.number)
      .map((s, i) => ({ ...s, number: i + 1 }));
  };

  const moveSetById = (id: string, direction: "up" | "down") => {
    setSets((prev) => {
      const index = prev.findIndex((s) => s.id === id);
      if (index < 0) return prev;

      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;

      const reordered = [...prev];
      const [item] = reordered.splice(index, 1);
      reordered.splice(targetIndex, 0, item);

      const normalized = reordered.map((s, i) => ({ ...s, number: i + 1 }));

      const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
      if (selectedPractice.isDraft) {
        localStorage.setItem(
          "selected_practice",
          JSON.stringify({
            ...selectedPractice,
            sets: normalized,
          })
        );
      }

      return normalized;
    });

    isManuallyModifiedRef.current = true;
    setIsDirtySets(true);
  };

  const ensureSetsExist = (): boolean => {
  if (!sets || sets.length === 0) {
    setIsEmptySetsDialogOpen(true);
    return false;
  }
  return true;
};


const [isLadderDialogOpen, setIsLadderDialogOpen] = useState(false);
const [ladderRounds, setLadderRounds] = useState("1");
const [ladderReps, setLadderReps] = useState("1");
const [ladderDistance, setLadderDistance] = useState("25");
const [ladderMinutes, setLadderMinutes] = useState("0");
const [ladderSeconds, setLadderSeconds] = useState("30");
const [ladderEnergy, setLadderEnergy] = useState(preferredEnergyZone || "Warm Up");
const [ladderDescription, setLadderDescription] = useState("");
const [ladderDirection, setLadderDirection] = useState<"asc" | "desc">("asc");
const [isCreatingFromFavorite, setIsCreatingFromFavorite] = useState(false);

const toNonNegativeInt = (value: string) => {
  const n = Number(value);
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.trunc(n));
};

const MAX_REPS = 1000;
const MAX_DISTANCE = 10000;
const MAX_PRACTICE_NAME_LENGTH = 60;
const MAX_PRACTICE_DESCRIPTION_LENGTH = 500;

const validateReps = (value: string | number): number => {
  const num = typeof value === 'string' ? parseInt(value) || 0 : value;
  return Math.min(Math.max(0, num), MAX_REPS);
};

const validateDistance = (value: string | number): number => {
  const num = typeof value === 'string' ? parseInt(value) || 0 : value;
  return Math.min(Math.max(0, num), MAX_DISTANCE);
};

const hasNegativeValue = (...values: string[]) => {
  return values.some(v => {
    const n = Number(v);
    return !Number.isNaN(n) && n < 0;
  });
};

const loadAllPractices = async () => {
  try {
    setLoadingPractices(true);

    const repo = new PracticeRepository();
    const folderRepo = new FolderRepository();
    const userId =
      localStorage.getItem("selected_swimmer_id") ||
      localStorage.getItem("current_user_swimmer_id");

    if (!userId) {
      toast.error("User not found");
      return;
    }

    const result = await repo.getAllPractices(userId);
    const folderData = await folderRepo.getAllFolders(Number(userId));
    const localFavoriteFolderIds = new Set(
      (JSON.parse(localStorage.getItem("favorite_folder_practice_model_ids") || "[]") || [])
        .map((v: any) => String(v))
    );

    const favoriteNormalPractices = (result.practices || []).filter(
      (p) => p.isFavorite === true
    );

    const favoriteFoldersList = (folderData.folders || []).filter(
      (f: any) =>
        f?.isFavorite === true ||
        f?.isFavorite === 1 ||
        String(f?.isFavorite ?? "").trim() === "1"
    );

    let favoriteFolderPractices = (folderData.practices || [])
      .filter((p: any) =>
        p?.isFavorite === true ||
        p?.isFavorite === 1 ||
        String(p?.isFavorite ?? "").trim() === "1" ||
        localFavoriteFolderIds.has(
          String(
            p?.folder_practice_model_id ??
              p?.folderPracticeModelId ??
              p?.id ??
              p?.practice_id ??
              ""
          )
        )
      )
      .map((p: any) => {
        const folderModelId = String(
          p.folder_practice_model_id ??
            p.folderPracticeModelId ??
            p.id ??
            p.practice_id ??
            ""
        );

        const mappedPractice = Practice.fromData({
          id: String(p.folder_practice_id ?? p.folderPracticeId ?? folderModelId),
          practice_id: folderModelId,
          coach_id: Number(p.coach_id || 0),
          swimmer_id: Number(p.swimmer_id || 0),
          name: p.name || "Unnamed Practice",
          description: p.description || "",
          practice_date: p.practice_date || p.date || "",
          practice_time: p.practice_time?.toString() || "0",
          practice_CourseTypeLookupId: Number(
            p.CourseTypeLookupId ?? p.practice_CourseTypeLookupId ?? 0
          ),
          distance: Number(p.distance) || 0,
          calories: Number(p.calories) || 0,
          createdAt: p.createdAt || p.created_at || "",
          updatedAt: p.updatedAt || p.updated_at || "",
          isFavorite: true,
          imageData:
            p.imageData ??
            p.image_data ??
            p.practice_imageData ??
            p.practiceImageData ??
            null,
        });

        (mappedPractice as any).logicalPracticeId = folderModelId;
        (mappedPractice as any).folder_practice_model_id = folderModelId;
        (mappedPractice as any).folder_practice_id = String(
          p.folder_practice_id ?? p.folderPracticeId ?? mappedPractice.id
        );
        (mappedPractice as any).folder_id = String(p.folder_id ?? p.folderId ?? "");
        return mappedPractice;
      });

    // Fallback: some aggregate APIs omit `isFavorite` in practices payload.
    // In that case, fetch folder practice lists and read favorite from practice_model.
    if (favoriteFolderPractices.length === 0 && (folderData.folders || []).length > 0) {
      const folderPracticeLists = await Promise.all(
        (folderData.folders || []).map((f: any) =>
          folderRepo.getPracticesByFolderId(Number(f.folder_id))
        )
      );

      const flattened = folderPracticeLists.flat();
      favoriteFolderPractices = flattened
        .filter((p: any) =>
          p?.isFavorite === true ||
          p?.isFavorite === 1 ||
          String(p?.isFavorite ?? "").trim() === "1" ||
          localFavoriteFolderIds.has(
            String(
              p?.folder_practice_model_id ??
                p?.folderPracticeModelId ??
                p?.id ??
                p?.practice_id ??
                ""
            )
          )
        )
        .map((p: any) => {
          const folderModelId = String(
            p.folder_practice_model_id ??
              p.folderPracticeModelId ??
              p.id ??
              p.practice_id ??
              ""
          );

          const mappedPractice = Practice.fromData({
            id: String(p.folder_practice_id ?? p.folderPracticeId ?? folderModelId),
            practice_id: folderModelId,
            coach_id: Number(p.coach_id || 0),
            swimmer_id: Number(p.swimmer_id || 0),
            name: p.name || "Unnamed Practice",
            description: p.description || "",
            practice_date: p.practice_date || p.date || "",
            practice_time: p.practice_time?.toString() || "0",
            practice_CourseTypeLookupId: Number(
              p.CourseTypeLookupId ?? p.practice_CourseTypeLookupId ?? 0
            ),
            distance: Number(p.distance) || 0,
            calories: Number(p.calories) || 0,
            createdAt: p.createdAt || p.created_at || "",
            updatedAt: p.updatedAt || p.updated_at || "",
            isFavorite: true,
            imageData:
              p.imageData ??
              p.image_data ??
              p.practice_imageData ??
              p.practiceImageData ??
              null,
          });

          (mappedPractice as any).logicalPracticeId = folderModelId;
          (mappedPractice as any).folder_practice_model_id = folderModelId;
          (mappedPractice as any).folder_practice_id = String(
            p.folder_practice_id ?? p.folderPracticeId ?? mappedPractice.id
          );
          (mappedPractice as any).folder_id = String(p.folder_id ?? p.folderId ?? "");
          return mappedPractice;
        });
    }

    const normalizedPractices = favoriteNormalPractices.map((p: Practice) => {
      (p as any).logicalPracticeId = p.practiceId;
      return p;
    });

    const mergedPractices = [...normalizedPractices, ...favoriteFolderPractices];
    const dedupedPractices = Array.from(
      new Map(
        mergedPractices.map((p: any) => {
          const key = String(
            p.logicalPracticeId ??
              p.folder_practice_model_id ??
              p.practiceId ??
              p.practice_id ??
              p.id
          );
          return [key, p] as const;
        })
      ).values()
    );

    setAllPractices(dedupedPractices);
    setAllPracticeLaps([...(result.laps || []), ...(folderData.laps || [])]);
    setFavoriteFolders(favoriteFoldersList);
    setActiveFavoriteFolderId(null);
    setFavoriteFolderPractices([]);
    setIsOpenPracticeDialogOpen(true);

  } catch (err) {
    console.error(err);
    toast.error("Failed to load practices");
  } finally {
    setLoadingPractices(false);
  }
};



const createLadderSets = () => {
  if (
    hasNegativeValue(
      ladderRounds,
      ladderReps,
      ladderDistance,
      ladderMinutes,
      ladderSeconds
    )
  ) {
    toast.error("Negative values are not allowed");
    return;
  }

  const rounds = toNonNegativeInt(ladderRounds);
  const reps = validateReps(ladderReps);
  const minutes = toNonNegativeInt(ladderMinutes);
  const seconds = toNonNegativeInt(ladderSeconds);
  const distance = validateDistance(ladderDistance);

  // Validate maximums
  if (reps !== toNonNegativeInt(ladderReps)) {
    toast.error(`Reps cannot exceed ${MAX_REPS}`);
    return;
  }
  if (distance !== toNonNegativeInt(ladderDistance)) {
    toast.error(`Distance cannot exceed ${MAX_DISTANCE} meters`);
    return;
  }
  const maxLadderDistance = distance * reps;
  if (maxLadderDistance > MAX_DISTANCE) {
    toast.error(
      `Ladder distance cannot exceed ${MAX_DISTANCE} meters. Reduce reps or distance.`
    );
    return;
  }

  const totalLadders = rounds * reps;
  if (totalLadders < 1) {
    toast.error("Rounds and reps must be at least 1");
    return;
  }

  const startingNumber = sets.length + 1;
  const totalSets: SetItem[] = [];

  let index = 0;
  for (let round = 0; round < rounds; round++) {
    for (let step = 0; step < reps; step++) {
      const stepIndex = ladderDirection === "asc" ? step : reps - 1 - step;
      const stepDistance = distance * (stepIndex + 1);
      totalSets.push({
        id: `${Date.now()}-${round}-${step}`,
        number: startingNumber + index,
        reps: 1,
        minutes,
        seconds,
        distance: stepDistance,
        energy_zone: ladderEnergy,
        description: ladderDescription || `Ladder ${index + 1}`,
      });
      index += 1;
    }
  }

  setSets(prev => {
    const updated = normalizeSets([...prev, ...totalSets]);
    isManuallyModifiedRef.current = true;
    
    // Update localStorage if this is a draft practice
    const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
    if (selectedPractice.isDraft) {
      const updatedPractice = {
        ...selectedPractice,
        sets: updated,
      };
      localStorage.setItem("selected_practice", JSON.stringify(updatedPractice));
    }
    
    return updated;
  });
  //setNoLaps(false);
};



const createSingleSet = () => {
  const hasNegativeDraftValue = splitSetDrafts.some((draft) =>
    hasNegativeValue(
      singleRounds,
      draft.reps,
      draft.distance,
      draft.minutes,
      draft.seconds
    )
  );

  if (hasNegativeDraftValue) {
    toast.error("Negative values are not allowed");
    return;
  }

  const rounds = toNonNegativeInt(singleRounds);

  if (rounds < 1) {
    toast.error("Rounds must be at least 1");
    return;
  }

  if (splitSetDrafts.length === 0) {
    toast.error("Add at least one set");
    return;
  }

  let normalizedDrafts: Array<{
    reps: number;
    distance: number;
    minutes: number;
    seconds: number;
    energy_zone: string;
    description: string;
  }> = [];

  try {
    normalizedDrafts = splitSetDrafts.map((draft) => {
      const reps = validateReps(draft.reps);
      const distance = validateDistance(draft.distance);

      if (reps !== toNonNegativeInt(draft.reps)) {
        throw new Error(`Reps cannot exceed ${MAX_REPS}`);
      }

      if (distance !== toNonNegativeInt(draft.distance)) {
        throw new Error(`Distance cannot exceed ${MAX_DISTANCE} meters`);
      }

      return {
        reps,
        distance,
        minutes: toNonNegativeInt(draft.minutes),
        seconds: toNonNegativeInt(draft.seconds),
        energy_zone: draft.energy_zone,
        description: draft.description,
      };
    });
  } catch (error) {
    toast.error(error instanceof Error ? error.message : "Invalid split values");
    return;
  }

  setSets(prev => {
    const startingNumber = prev.length + 1;
    const createdSets: SetItem[] = [];

    for (let round = 0; round < rounds; round++) {
      normalizedDrafts.forEach((draft, draftIndex) => {
        const setNumber = startingNumber + createdSets.length;
        createdSets.push({
          id: `${Date.now()}-${round}-${draftIndex}`,
          number: setNumber,
          reps: draft.reps,
          minutes: draft.minutes,
          seconds: draft.seconds,
          distance: draft.distance,
          energy_zone: draft.energy_zone,
          description: draft.description || `Set ${setNumber}`,
        });
      });
    }

    const updated = normalizeSets([...prev, ...createdSets]);
    isManuallyModifiedRef.current = true;
    
    // Update localStorage if this is a draft practice
    const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
    if (selectedPractice.isDraft) {
      const updatedPractice = {
        ...selectedPractice,
        sets: updated,
      };
      localStorage.setItem("selected_practice", JSON.stringify(updatedPractice));
    }
    
    return updated;
  });
  resetSingleSetForm();
  //setNoLaps(false);
};

 const resetSingleSetForm = () => {
  setSingleRounds("1");
  setSplitSetDrafts([createDefaultSplitDraft(preferredEnergyZone)]);
  setSingleFormError(null);
};

const addSplitSetDraft = () => {
  setSplitSetDrafts((prev) => [...prev, createDefaultSplitDraft(preferredEnergyZone)]);
};

const removeSplitSetDraft = (draftId: string) => {
  setSplitSetDrafts((prev) =>
    prev.length > 1 ? prev.filter((draft) => draft.id !== draftId) : prev
  );
};

const updateSplitSetDraft = (
  draftId: string,
  field: keyof Omit<SplitDraftItem, "id">,
  value: string
) => {
  setSplitSetDrafts((prev) =>
    prev.map((draft) => (draft.id === draftId ? { ...draft, [field]: value } : draft))
  );
};

const resetLadderForm = () => {
  setLadderRounds("1");
  setLadderReps("1");
  setLadderDistance("25");
  setLadderMinutes("0");
  setLadderSeconds("30");
  setLadderEnergy(preferredEnergyZone || "Warm Up");
  setLadderDescription("");
  setLadderDirection("asc");
  setLadderFormError(null);
};


  const calculateTotalTime = () => {
    const totalSeconds = sets.reduce((total, set) => {
      const setTotalSeconds = set.reps * (set.minutes * 60 + set.seconds);
      return total + setTotalSeconds;
    }, 0);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const calculateTotalDistance = () => {
  return sets.reduce((sum, set) => {
    const distance =
      typeof set.distance === "number" ? set.distance : 0;

    const reps =
      typeof set.reps === "number" ? set.reps : 0;

    return sum + reps * distance;
  }, 0);
};

  const favoriteRootPractices = allPractices.filter(
    (p: any) => !p.folder_id && !p.folder_practice_model_id
  );

  const calculateTotalReps = () => {
    return sets.reduce((sum, set) => {
      const reps = typeof set.reps === "number" ? set.reps : 0;
      return sum + reps;
    }, 0);
  };


  const updateSet = (id: string, field: keyof SetItem, value: any) => {
    let validatedValue = value;
    
    // Apply maximum validation for reps and distance
    if (field === 'reps') {
      validatedValue = validateReps(value);
      if (validatedValue !== value) {
        toast.error(`Reps cannot exceed ${MAX_REPS}`);
      }
    } else if (field === 'distance') {
      validatedValue = validateDistance(value);
      if (validatedValue !== value) {
        toast.error(`Distance cannot exceed ${MAX_DISTANCE} meters`);
      }
    }
    
    setSets((prev) => {
      const updated = normalizeSets(
        prev.map((set) => (set.id === id ? { ...set, [field]: validatedValue } : set))
      );
      
      isManuallyModifiedRef.current = true;
      
      // Update localStorage if this is a draft practice
      const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
      if (selectedPractice.isDraft) {
        const updatedPractice = {
          ...selectedPractice,
          sets: updated,
        };
        localStorage.setItem("selected_practice", JSON.stringify(updatedPractice));
      }
      
      return updated;
    });
  };

  const addDefaultSet = () => {
    if (addDefaultSetLockRef.current) return;
    addDefaultSetLockRef.current = true;
    setTimeout(() => {
      addDefaultSetLockRef.current = false;
    }, 50);

    const newSet: SetItem = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      number: sets.length + 1,
      reps: 1,
      minutes: 0,
      seconds: 0,
      distance: 0,
      energy_zone: preferredEnergyZone || "Warm Up",
      description: "",
    };

    setSets((prev) => {
      const updated = normalizeSets([...prev, newSet]);
      isManuallyModifiedRef.current = true;

      const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
      if (selectedPractice.isDraft) {
        const updatedPractice = {
          ...selectedPractice,
          sets: updated,
        };
        localStorage.setItem("selected_practice", JSON.stringify(updatedPractice));
      }

      return updated;
    });
    setNoLaps(false);
  };

  const handleCopySet = (source: SetItem) => {
    const newSet: SetItem = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      number: sets.length + 1,
      reps: source.reps,
      minutes: source.minutes,
      seconds: source.seconds,
      distance: source.distance,
      energy_zone: source.energy_zone,
      description: source.description || "",
    };

    setSets((prev) => {
      const updated = normalizeSets([...prev, newSet]);
      isManuallyModifiedRef.current = true;

      const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
      if (selectedPractice.isDraft) {
        const updatedPractice = {
          ...selectedPractice,
          sets: updated,
        };
        localStorage.setItem("selected_practice", JSON.stringify(updatedPractice));
      }

      return updated;
    });

    setNoLaps(false);
  };


  const [isNewPracticeDialogOpen, setIsNewPracticeDialogOpen] = useState(false);
  const [newPracticeName, setNewPracticeName] = useState("");
  const [newPracticeDescription, setNewPracticeDescription] = useState("");
  const [isFavorite, setIsFavorite] = useState(false);
  const [pendingFavorite, setPendingFavorite] = useState<boolean | null>(null);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false)
  const logSetSave = (...args: any[]) => {
    console.log("[SetsSaveDebug]", ...args);
  };

  console.log(pendingFavorite);

  useEffect(() => {
    const selected = JSON.parse(localStorage.getItem("selected_practice") || "{}");
    if (selected && selected.isFavorite !== undefined) {
      setIsFavorite(!!selected.isFavorite);
    } else {
      setIsFavorite(false); 
    }
  }, [practiceId]);

const withTimeout = async <T,>(
  promise: Promise<T>,
  timeoutMs = 30000,
  timeoutMessage = "Request timed out. Please try again."
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const resolveFolderId = (): string | null => {
  const propFolder = folderId == null ? "" : String(folderId).trim();
  if (
    propFolder &&
    propFolder !== "0" &&
    propFolder.toLowerCase() !== "null" &&
    propFolder.toLowerCase() !== "undefined"
  ) {
    return propFolder;
  }

  const selectedPractice =
    JSON.parse(localStorage.getItem("selected_practice") || "{}");
  const rawFolderId =
    selectedPractice.folder_id ??
    selectedPractice.folderId ??
    null;

  const normalizedFolderId = rawFolderId == null ? "" : String(rawFolderId).trim();
  if (
    normalizedFolderId &&
    normalizedFolderId !== "0" &&
    normalizedFolderId.toLowerCase() !== "null" &&
    normalizedFolderId.toLowerCase() !== "undefined"
  ) {
    return normalizedFolderId;
  }

  return null;
};

const resolveSelectedPracticeImage = (): string | null => {
  const selected = JSON.parse(localStorage.getItem("selected_practice") || "{}");
  const raw =
    selected.imageData ??
    selected.image_data ??
    selected.practice_imageData ??
    selected.practiceImageData ??
    "";
  const normalized =
    typeof raw === "string" ? raw.trim().replace(/\\\//g, "/") : "";
  if (!normalized || normalized.toLowerCase() === "null") return null;
  return normalized;
};

const toSqlDateTime = (value?: unknown): string => {
  const raw = String(value ?? "").trim();

  if (!raw) {
    return new Date().toLocaleString("sv-SE", { timeZone: "UTC" }).replace("T", " ");
  }

  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)) {
    return raw;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return `${raw} 00:00:00`;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const [month, day, year] = raw.split("/");
    return `${year}-${month}-${day} 00:00:00`;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toLocaleString("sv-SE", { timeZone: "UTC" }).replace("T", " ");
  }

  return parsed.toLocaleString("sv-SE", { timeZone: "UTC" }).replace("T", " ");
};

const validatePracticeMetadata = (
  rawName?: string,
  rawDescription?: string
): boolean => {
  const normalizedName = String(rawName ?? "").trim();
  const normalizedDescription = String(rawDescription ?? "");

  if (!normalizedName) {
    toast.error("Practice name is required.");
    return false;
  }

  if (normalizedName.length > MAX_PRACTICE_NAME_LENGTH) {
    toast.error(`Practice name must be ${MAX_PRACTICE_NAME_LENGTH} characters or fewer.`);
    return false;
  }

  if (normalizedDescription.length > MAX_PRACTICE_DESCRIPTION_LENGTH) {
    toast.error(
      `Practice description must be ${MAX_PRACTICE_DESCRIPTION_LENGTH} characters or fewer.`
    );
    return false;
  }

  return true;
};

const handleSaveLaps = async () => {
  if (loadingAction) return;
  if (!ensureSetsExist()) return;

  try {
    setLoadingAction(true);
    logSetSave("handleSaveLaps:start", {
      localPracticeId,
      propFolderId: folderId,
      setsCount: sets.length,
      isFromFavoriteTemplate,
      name,
      description,
    });

    // New practice or favorite template → open dialog
    if (!localPracticeId || isFromFavoriteTemplate) {
      logSetSave("handleSaveLaps:new-practice-dialog", {
        reason: !localPracticeId ? "missing_localPracticeId" : "favorite_template",
      });
      setIsNewPracticeDialogOpen(true);
      return;
    }

    const resolvedFolderId = resolveFolderId();
    logSetSave("handleSaveLaps:resolvedFolderId", { resolvedFolderId });

    await withTimeout(
      savePracticeAndLaps(
        localPracticeId,
        name,
        description,
        undefined,
        resolvedFolderId
      ),
      30000,
      "Save is taking too long. Please try again."
    );

    setIsDirtySets(false);
    logSetSave("handleSaveLaps:success");
    onSectionChange?.("practice");
    onClose?.();

  } catch (error) {
    logSetSave("handleSaveLaps:error", error);
    toast.error("Failed to save laps. Please try again.");
  } finally {
    setLoadingAction(false);
    logSetSave("handleSaveLaps:end");
  }
};


const savePracticeAndLaps = async (
  practiceId: number | string,
  name?: string,
  description?: string,
  favorite?: boolean,
  folderId?: string | null,
  practiceImageFile?: File | null,
  practiceImageUrl?: string | null
): Promise<void> => {
  const repo = new PracticeRepository();
  const folderRepo = new FolderRepository();

  let uploadedPracticeImage: string | null = null;
  if (practiceImageUrl) {
    uploadedPracticeImage = practiceImageUrl;
  } else if (practiceImageFile) {
    const uploadResp = await repo.uploadPracticeImage(practiceImageFile);
    if (uploadResp.status !== 1 || !uploadResp.image_link) {
      throw new Error(uploadResp.msg || "Failed to upload practice image");
    }
    uploadedPracticeImage = uploadResp.image_link;
  }

  const selectedSwimmerId = localStorage.getItem("selected_swimmer_id");
  let coachId: number;
  let swimmerId: number;

  if (selectedSwimmerId) {
    coachId = 0;
    swimmerId = Number(selectedSwimmerId);
  } else {
    coachId = Number(localStorage.getItem("coach_id")) || 0;
    swimmerId = Number(localStorage.getItem("current_user_swimmer_id")) || 0;
  }

  const now = new Date();
  const nowISO = now.toISOString();
  logSetSave("savePracticeAndLaps:start", {
    incomingPracticeId: practiceId,
    incomingFolderId: folderId,
    swimmerId,
    coachId,
    favorite,
    name,
    description,
    setsCount: sets.length,
  });

  const formattedLaps = sets.map((s) => ({
    id: 0,
    practice_lap_id: 0,
    practice_id: "",
    rep: s.reps || 1,
    distance: Number(s.distance) || 0,
    interval: (s.minutes || 0) * 60 + (s.seconds || 0),
    energy_zone: s.energy_zone || "Warm Up",
    description: s.description || "",
    time: 0,
    createdAt: nowISO,
    updated_at: nowISO,
    passedRepNumber: 0,
    isNoEditable: false,
    isPassed: 0,
    user_id: swimmerId.toString(),
    coach_id: coachId.toString(),
  }));

  const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
  const resolvedImageData =
    uploadedPracticeImage ??
    selectedPractice.imageData ??
    selectedPractice.practice_imageData ??
    null;
  const parsePracticeDateForSave = (input: unknown): Date | null => {
    if (!input) return null;
    const raw = String(input).trim();
    if (!raw) return null;

    // Keep calendar day stable for YYYY-MM-DD values across timezones.
    const ymdMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymdMatch) {
      const year = Number(ymdMatch[1]);
      const month = Number(ymdMatch[2]);
      const day = Number(ymdMatch[3]);
      return new Date(year, month - 1, day, 12, 0, 0);
    }

    // MM/DD/YYYY from UI lists
    const mdyMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdyMatch) {
      const month = Number(mdyMatch[1]);
      const day = Number(mdyMatch[2]);
      const year = Number(mdyMatch[3]);
      return new Date(year, month - 1, day, 12, 0, 0);
    }

    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const selectedPracticeDateRaw =
    selectedPractice.practice_date ??
    selectedPractice.PracticeDate ??
    selectedPractice.date ??
    practiceDate ??
    null;
  const preservedPracticeDate = parsePracticeDateForSave(selectedPracticeDateRaw) ?? now;
  const preservedPracticeTime =
    (selectedPractice.practice_time || selectedPractice.practiceTime || practiceTime || "AM").toString();
  const preservedCourseTypeLookupId =
    Number(
      selectedPractice.courseTypeLookupId ??
      selectedPractice.CourseTypeLookupId ??
      courseTypeLookupId ??
      1
    ) || 1;
  logSetSave("savePracticeAndLaps:selectedPractice", {
    id: selectedPractice?.id,
    practice_id: selectedPractice?.practice_id,
    practiceId: selectedPractice?.practiceId,
    folder_id: selectedPractice?.folder_id,
    folderId: selectedPractice?.folderId,
    folder_practice_model_id: selectedPractice?.folder_practice_model_id,
    folder_practice_id: selectedPractice?.folder_practice_id,
    isDraft: selectedPractice?.isDraft,
    preservedPracticeDateRaw: selectedPracticeDateRaw,
    preservedPracticeDateISO: preservedPracticeDate.toISOString(),
    preservedPracticeTime,
    preservedCourseTypeLookupId,
  });
  const selectedFolderId =
    selectedPractice.folder_id ?? selectedPractice.folderId ?? null;
  const normalizedSelectedFolderId =
    selectedFolderId == null ? "" : String(selectedFolderId).trim();
  let effectiveFolderId =
    folderId && String(folderId).trim()
      ? String(folderId).trim()
      : normalizedSelectedFolderId && normalizedSelectedFolderId !== "0"
      ? normalizedSelectedFolderId
      : null;

  const isFolderPracticeContext = Boolean(
    effectiveFolderId ||
      selectedPractice.isFolderPractice === true ||
      ((selectedPractice.folder_practice_model_id || selectedPractice.folder_practice_id) &&
        (selectedPractice.folder_id || selectedPractice.folderId))
  );
  logSetSave("savePracticeAndLaps:resolvedContext", {
    normalizedSelectedFolderId,
    effectiveFolderId,
    isFolderPracticeContext,
  });

  // =====================================================
  // =============== FOLDER PRACTICE FLOW ================
  // =====================================================
  if (isFolderPracticeContext) {
    // Keep practice inside the SAME folder and update in place when possible.
    // Reuse existing folder_practice_model_id to avoid creating duplicates.
    let targetPracticeId =
      String(
        selectedPractice.folder_practice_model_id ??
          selectedPractice.practiceId ??
          selectedPractice.practice_id ??
          practiceId ??
          ""
      ).trim() || (Math.floor(Math.random() * 90000) + 10000).toString();
    logSetSave("folderFlow:initialTargetPracticeId", { targetPracticeId });

    if (!effectiveFolderId) {
      const userIdForFolderLookup = swimmerId || coachId;
      if (userIdForFolderLookup) {
        try {
          logSetSave("folderFlow:lookupFolderMapping:start", { userIdForFolderLookup });
          const folderData = await withTimeout(
            folderRepo.getAllFoldersPaginated(userIdForFolderLookup, 1, 500),
            30000,
            "Fetching folder data timed out."
          );

          const candidateIds = new Set(
            [
              String(practiceId || "").trim(),
              String(selectedPractice.folder_practice_model_id || "").trim(),
              String(selectedPractice.folder_practice_id || "").trim(),
              String(selectedPractice.practiceId || "").trim(),
              String(selectedPractice.practice_id || "").trim(),
              String(selectedPractice.id || "").trim(),
            ].filter(Boolean)
          );

          const matchedPractice = (folderData?.practices || []).find((p: any) => {
            const modelId = String(p?.folder_practice_model_id ?? "").trim();
            const practiceIdValue = String(p?.folder_practice_id ?? "").trim();
            return candidateIds.has(modelId) || candidateIds.has(practiceIdValue);
          });
          logSetSave("folderFlow:lookupFolderMapping:result", {
            candidateIds: Array.from(candidateIds),
            matched: matchedPractice
              ? {
                  folder_id: matchedPractice.folder_id,
                  folder_practice_model_id: matchedPractice.folder_practice_model_id,
                  folder_practice_id: matchedPractice.folder_practice_id,
                }
              : null,
          });

          if (matchedPractice) {
            const matchedFolderId = String(matchedPractice.folder_id ?? "").trim();
            const matchedModelId = String(
              matchedPractice.folder_practice_model_id ?? ""
            ).trim();

            if (matchedFolderId && matchedFolderId !== "0") {
              effectiveFolderId = matchedFolderId;
            }
            if (matchedModelId) {
              targetPracticeId = matchedModelId;
            }
          }
        } catch (lookupErr) {
          console.warn("Failed to resolve folder mapping for practice:", lookupErr);
          logSetSave("folderFlow:lookupFolderMapping:error", lookupErr);
        }
      }
    }

    const targetFolderId = effectiveFolderId;
    logSetSave("folderFlow:resolvedTargets", {
      targetFolderId,
      targetPracticeId,
    });

    if (!targetFolderId) {
      logSetSave("folderFlow:missingFolderId:error", {
        selectedPractice,
        incomingFolderId: folderId,
      });
      throw new Error("Folder practice save failed: missing folder_id.");
    }

    // Folder flow follows replace semantics used elsewhere:
    // remove existing folder practice model, then recreate with updated laps.
    try {
      logSetSave("folderFlow:deleteFolderPractice:start", { targetPracticeId });
      await withTimeout(
        folderRepo.deleteFolderPractice(targetPracticeId),
        20000,
        "Deleting old folder practice timed out."
      );
      logSetSave("folderFlow:deleteFolderPractice:success", { targetPracticeId });
    } catch (err) {
      // New folder practice may not exist yet; continue with create.
      console.warn("Folder practice delete skipped/failed:", err);
      logSetSave("folderFlow:deleteFolderPractice:error", err);
    }

    formattedLaps.forEach((lap) => {
      lap.practice_id = targetPracticeId;
    });

    const practiceModel = Practice.create({
      id: targetPracticeId,
      practiceId: targetPracticeId,
      coachId,
      swimmerId,
      name: name || "Untitled Practice",
      description: description || "",
      date: preservedPracticeDate,
      practice_time: preservedPracticeTime,
      courseTypeLookupId: preservedCourseTypeLookupId,
      isFavorite: favorite ?? false,
      isFinished: false,
      imageData: resolvedImageData,
      folderId: targetFolderId,
    });
    logSetSave("folderFlow:storeFolderPractice:request", {
      endpoint: "folderpractice/manage_folder_practice_model",
      targetPracticeId,
      targetFolderId,
      lapsCount: formattedLaps.length,
      practiceName: practiceModel.name,
    });

    const response = await withTimeout(
      repo.storeFolderPractice(
        coachId,
        swimmerId,
        practiceModel,
        formattedLaps
      ),
      30000,
      "Saving practice timed out."
    );
    logSetSave("folderFlow:storeFolderPractice:response", {
      responseId: (response as any)?.id,
      responsePracticeId: (response as any)?.practiceId,
      responseFolderId: (response as any)?.folderId ?? (response as any)?.folder_id,
    });

    localStorage.setItem(
      "selected_practice",
      JSON.stringify({
        ...response,
        folder_id: targetFolderId,
        folder_practice_model_id: targetPracticeId,
        isFolderPractice: true,
        isFavorite: favorite ?? false,
        imageData: resolvedImageData,
      })
    );

    toast.success("Folder practice saved");
    logSetSave("folderFlow:success");
    return;
  }

  // =====================================================
  // =============== NORMAL PRACTICE FLOW =================
  // =====================================================
  const oldPracticeId = String(practiceId);
  const newPracticeId = (Math.floor(Math.random() * 90000) + 10000).toString();

  formattedLaps.forEach((lap) => {
    lap.practice_id = newPracticeId;
  });

  const practiceModel = Practice.create({
    id: newPracticeId,
    practiceId: newPracticeId,
    coachId,
    swimmerId,
    name: name || "Untitled Practice",
    description: description || "",
    date: preservedPracticeDate,
    practice_time: preservedPracticeTime,
    courseTypeLookupId: preservedCourseTypeLookupId,
    isFavorite: favorite ?? false,
    isFinished: false,
    imageData: resolvedImageData,
  });
  logSetSave("normalFlow:savePractice:request", {
    endpoint: "practice/manage_coach_practice",
    newPracticeId,
    lapsCount: formattedLaps.length,
    practiceName: practiceModel.name,
  });

  const response = await withTimeout(
    repo.save(
      coachId,
      swimmerId,
      practiceModel,
      formattedLaps
    ),
    30000,
    "Saving practice timed out."
  );
  logSetSave("normalFlow:savePractice:response", {
    responseId: (response as any)?.id,
    responsePracticeId: (response as any)?.practiceId,
  });

  const savedPracticeId = String(
    (response as any)?.practiceId ??
    (response as any)?.practice_id ??
    (response as any)?.id ??
    newPracticeId
  );

  if (savedPracticeId !== oldPracticeId) {
    try {
      logSetSave("normalFlow:deleteOldPractice:start", {
        oldPracticeId,
        savedPracticeId,
      });
      await withTimeout(
        repo.delete(oldPracticeId, swimmerId),
        20000,
        "Deleting old practice timed out."
      );
      logSetSave("normalFlow:deleteOldPractice:success", { oldPracticeId });
    } catch (err) {
      logSetSave("normalFlow:deleteOldPractice:error", err);
      throw err;
    }
  }

  localStorage.setItem(
    "selected_practice",
    JSON.stringify({
      ...response,
      isFolderPractice: false,
      isFavorite: favorite ?? false,
      imageData: resolvedImageData,
    })
  );

  toast.success("Practice saved");
  logSetSave("normalFlow:success");
};

const loadFavoriteFolderPractices = async (folderId: string) => {
  try {
    setLoadingFavoriteFolderPractices(true);
    const folderRepo = new FolderRepository();
    const practices = await folderRepo.getPracticesByFolderId(Number(folderId));
    setFavoriteFolderPractices(practices || []);
    setActiveFavoriteFolderId(folderId);
  } catch (err) {
    console.error(err);
    toast.error("Failed to load folder practices");
  } finally {
    setLoadingFavoriteFolderPractices(false);
  }
};

const createFavoriteFolderAndMovePractice = async () => {
  if (!selectedFavoritePracticeId) {
    toast.error("Select a practice to move");
    return;
  }

  const folderName = favoriteFolderName.trim();
  if (!folderName) {
    toast.error("Folder name is required");
    return;
  }

  const selectedPractice = favoriteRootPractices.find(
    (p) =>
      String(
        (p as any).logicalPracticeId ??
          (p as any).practice_id ??
          (p as any).practiceId ??
          (p as any).id ??
          ""
      ).trim() === String(selectedFavoritePracticeId).trim()
  );

  if (!selectedPractice) {
    toast.error("Practice not found");
    return;
  }

  if ((selectedPractice as any).folder_id || (selectedPractice as any).folder_practice_model_id) {
    toast.info("Practice is already in a folder");
    return;
  }

  try {
    setLoadingAction(true);

    const selectedSwimmerId = localStorage.getItem("selected_swimmer_id");
    const swimmerId = selectedSwimmerId
      ? Number(selectedSwimmerId)
      : Number(localStorage.getItem("current_user_swimmer_id")) || 0;
    const coachId = selectedSwimmerId ? 0 : Number(localStorage.getItem("coach_id")) || 0;

    const folderRepo = new FolderRepository();
    const practiceRepo = new PracticeRepository();

    const localFolderId = `folder-${Date.now()}`;
    const folder = await folderRepo.manageFolder(
      coachId,
      swimmerId,
      localFolderId,
      folderName,
      favoriteFolderDescription.trim(),
      true
    );

    const targetFolderId = String(folder.folder_id ?? localFolderId);
    const practiceId = String(
      (selectedPractice as any).logicalPracticeId ??
        (selectedPractice as any).practice_id ??
        (selectedPractice as any).practiceId ??
        (selectedPractice as any).id ??
        ""
    ).trim();

    if (!practiceId) {
      toast.error("Practice not found");
      return;
    }

    const practiceLaps = (allPracticeLaps || []).filter((lap) => {
      const lapIds = [
        lap.practice_id,
        (lap as any).practiceId,
        (lap as any).folder_practice_model_id,
      ]
        .filter(Boolean)
        .map(String);
      return lapIds.includes(practiceId);
    });

    const newModelId = crypto.randomUUID();
    const normalizedLaps = practiceLaps.map((lap: any) => ({
      createdAt: lap.createdAt ?? lap.createdAtString ?? new Date().toISOString(),
      description: lap.description ?? "",
      distance: lap.distance?.toString() ?? "0",
      energy_zone: lap.energy_zone ?? "",
      folder_practice_lap_id: "0",
      folder_practice_model_id: newModelId,
      interval: lap.interval_lap ?? lap.interval ?? "0",
      isnoEditable: lap.isNoEditable ?? "0",
      passedRapNumber: lap.passedRepNumber ?? "0",
      rep: lap.rep?.toString() ?? "1",
    }));

    const parsedDate = selectedPractice.date ?? new Date();
    const safeDate = Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;

    const practiceModel = Practice.create({
      id: newModelId,
      practiceId: newModelId,
      coachId,
      swimmerId,
      name: selectedPractice.name ?? "",
      description: selectedPractice.description ?? "",
      date: safeDate,
      practice_time: selectedPractice.practice_time ?? "AM",
      courseTypeLookupId: Number(
        selectedPractice.courseTypeLookupId ?? 1
      ),
      folderId: targetFolderId,
      isFavorite: true,
      isFinished: false,
      imageData: null,
    });

    await practiceRepo.storeFolderPractice(coachId, swimmerId, practiceModel, normalizedLaps);

    const key = "favorite_folder_practice_model_ids";
    const existing = JSON.parse(localStorage.getItem(key) || "[]");
    const normalized = Array.isArray(existing)
      ? existing.map((v: any) => String(v))
      : [];
    if (!normalized.includes(String(newModelId))) {
      normalized.push(String(newModelId));
    }
    localStorage.setItem(key, JSON.stringify(normalized));

    await practiceRepo.delete(practiceId, swimmerId);

    toast.success("Practice moved to favorite folder");
    setIsCreateFavoriteFolderDialogOpen(false);
    setFavoriteFolderName("");
    setFavoriteFolderDescription("");
    setSelectedFavoritePracticeId("");
    await loadAllPractices();
  } catch (err) {
    console.error(err);
    toast.error("Failed to create folder or move practice");
  } finally {
    setLoadingAction(false);
  }
};

const openRenameDialog = (type: "folder" | "practice" | "folderPractice", data: any) => {
  setRenameTarget({ type, data });
  setRenameName(data?.name ?? "");
  setRenameDescription(data?.description ?? "");
  setIsRenameDialogOpen(true);
};

const handleRenameSave = async () => {
  if (!renameTarget) return;
  const name = renameName.trim();
  if (!name) {
    toast.error("Name is required");
    return;
  }

  try {
    setLoadingAction(true);
    const selectedSwimmerId = localStorage.getItem("selected_swimmer_id");
    const swimmerId = selectedSwimmerId
      ? Number(selectedSwimmerId)
      : Number(localStorage.getItem("current_user_swimmer_id")) || 0;
    const coachId = selectedSwimmerId ? 0 : Number(localStorage.getItem("coach_id")) || 0;

    if (renameTarget.type === "folder") {
      const folderRepo = new FolderRepository();
      const folderId = String(renameTarget.data.folder_id ?? renameTarget.data.id ?? "");
      if (!folderId) {
        toast.error("Folder not found");
        return;
      }
      const folderPractices = await folderRepo.getPracticesByFolderId(Number(folderId));

      await folderRepo.deleteFolder(Number(folderId));

      const newLocalFolderId = `folder-${Date.now()}`;
      const newFolder = await folderRepo.manageFolder(
        coachId,
        swimmerId,
        newLocalFolderId,
        name,
        renameDescription.trim(),
        true
      );

      const targetFolderId = String(newFolder.folder_id ?? newLocalFolderId);

      const favoriteKey = "favorite_folder_practice_model_ids";
      const existingFavorites = JSON.parse(localStorage.getItem(favoriteKey) || "[]");
      const favoriteIds = Array.isArray(existingFavorites)
        ? existingFavorites.map((v: any) => String(v))
        : [];

      for (const practice of folderPractices || []) {
        const oldModelId = String(
          practice.folder_practice_model_id ?? practice.id ?? ""
        ).trim();
        const newModelId = crypto.randomUUID();

        const practiceLaps = (allPracticeLaps || []).filter((lap) => {
          const lapIds = [
            lap.practice_id,
            (lap as any).practiceId,
            (lap as any).folder_practice_model_id,
          ]
            .filter(Boolean)
            .map(String);
          return lapIds.includes(oldModelId);
        });

        const normalizedLaps = practiceLaps.map((lap: any) => ({
          createdAt: lap.createdAt ?? new Date().toISOString(),
          description: lap.description ?? "",
          distance: lap.distance?.toString() ?? "0",
          energy_zone: lap.energy_zone ?? "",
          folder_practice_lap_id: "0",
          folder_practice_model_id: newModelId,
          interval: lap.interval_lap ?? lap.interval ?? "0",
          isnoEditable: lap.isNoEditable ?? "0",
          passedRapNumber: lap.passedRepNumber ?? "0",
          rep: lap.rep?.toString() ?? "1",
        }));

        const rawDate = practice.date ?? "";
        const parsedDate = rawDate ? new Date(rawDate) : new Date();
        const safeDate = Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;

        const practiceModel = Practice.create({
          id: newModelId,
          practiceId: newModelId,
          coachId,
          swimmerId,
          name: practice.name ?? "",
          description: practice.description ?? "",
          date: safeDate,
          practice_time: practice.practice_time ?? "AM",
          courseTypeLookupId: Number(practice.CourseTypeLookupId ?? 1),
          folderId: targetFolderId,
          isFavorite: true,
          isFinished: false,
          imageData: null,
        });

        const practiceRepo = new PracticeRepository();
        await practiceRepo.storeFolderPractice(coachId, swimmerId, practiceModel, normalizedLaps);

        const wasFavorite =
          practice.isFavorite === true ||
          practice.isFavorite === 1 ||
          String(practice.isFavorite ?? "").trim() === "1";

        if (wasFavorite) {
          if (!favoriteIds.includes(newModelId)) {
            favoriteIds.push(newModelId);
          }
          const oldIndex = favoriteIds.indexOf(oldModelId);
          if (oldIndex >= 0) favoriteIds.splice(oldIndex, 1);
        }
      }

      localStorage.setItem(favoriteKey, JSON.stringify(favoriteIds));
    } else if (renameTarget.type === "practice") {
      const practiceRepo = new PracticeRepository();
      const practiceId = String(
        renameTarget.data.logicalPracticeId ??
          renameTarget.data.practice_id ??
          renameTarget.data.practiceId ??
          renameTarget.data.id ??
          ""
      ).trim();
      if (!practiceId) {
        toast.error("Practice not found");
        return;
      }
      const practiceLaps = allPracticeLaps.filter((lap) => {
        const lapIds = [
          lap.practice_id,
          (lap as any).practiceId,
          (lap as any).folder_practice_model_id,
        ]
          .filter(Boolean)
          .map(String);
        return lapIds.includes(practiceId);
      });

      const lapModels = practiceLaps.map((lap: any) => ({
        id: 0,
        practice_lap_id: 0,
        practice_id: practiceId,
        rep: String(lap.rep ?? 1),
        distance: String(lap.distance ?? 0),
        interval: String(lap.interval_lap ?? lap.interval ?? 0),
        energy_zone: lap.energy_zone ?? "Warm Up",
        description: lap.description ?? "",
        time: Number(lap.time ?? 0),
        createdAt: lap.createdAt ?? new Date().toISOString(),
        updated_at: new Date().toISOString(),
        passedRepNumber: Number(lap.passedRepNumber ?? 0),
        isNoEditable: lap.isNoEditable ?? false,
        isPassed: Number(lap.isPassed ?? 0),
        user_id: String(swimmerId),
        coach_id: String(coachId),
      }));

      const practiceModel = Practice.create({
        id: practiceId,
        practiceId,
        coachId,
        swimmerId,
        name,
        description: renameDescription.trim(),
        date: new Date(renameTarget.data.date ?? Date.now()),
        practice_time: renameTarget.data.practice_time ?? "AM",
        courseTypeLookupId: Number(renameTarget.data.courseTypeLookupId ?? 1),
        isFavorite: true,
        isFinished: Boolean(renameTarget.data.isFinished),
        imageData: null,
      });

      await practiceRepo.delete(practiceId, swimmerId);
      await practiceRepo.save(coachId, swimmerId, practiceModel, lapModels, "");
    } else if (renameTarget.type === "folderPractice") {
      const practiceRepo = new PracticeRepository();
      const folderPracticeModelId = String(
        renameTarget.data.folder_practice_model_id ??
          renameTarget.data.folderPracticeModelId ??
          renameTarget.data.id ??
          ""
      ).trim();
      const folderId = String(renameTarget.data.folder_id ?? "");
      if (!folderPracticeModelId || !folderId) {
        toast.error("Folder practice not found");
        return;
      }

      const practiceLaps = allPracticeLaps.filter((lap) => {
        const lapIds = [
          lap.practice_id,
          (lap as any).practiceId,
          (lap as any).folder_practice_model_id,
        ]
          .filter(Boolean)
          .map(String);
        return lapIds.includes(folderPracticeModelId);
      });

      const normalizedLaps = practiceLaps.map((lap: any) => ({
        createdAt: lap.createdAt ?? new Date().toISOString(),
        description: lap.description ?? "",
        distance: lap.distance?.toString() ?? "0",
        energy_zone: lap.energy_zone ?? "",
        folder_practice_lap_id: "0",
        folder_practice_model_id: folderPracticeModelId,
        interval: lap.interval_lap ?? lap.interval ?? "0",
        isnoEditable: lap.isNoEditable ?? "0",
        passedRapNumber: lap.passedRepNumber ?? "0",
        rep: lap.rep?.toString() ?? "1",
      }));

      const practiceModel = Practice.create({
        id: folderPracticeModelId,
        practiceId: folderPracticeModelId,
        coachId,
        swimmerId,
        name,
        description: renameDescription.trim(),
        date: new Date(renameTarget.data.date ?? Date.now()),
        practice_time: renameTarget.data.practice_time ?? "AM",
        courseTypeLookupId: Number(renameTarget.data.courseTypeLookupId ?? 1),
        folderId,
        isFavorite: true,
        isFinished: Boolean(renameTarget.data.isFinished),
        imageData: null,
      });

      const folderRepo = new FolderRepository();
      await folderRepo.deleteFolderPractice(folderPracticeModelId);
      await practiceRepo.storeFolderPractice(coachId, swimmerId, practiceModel, normalizedLaps);
    }

    toast.success("Updated successfully");
    setIsRenameDialogOpen(false);
    setRenameTarget(null);
    setRenameName("");
    setRenameDescription("");
    await loadAllPractices();
  } catch (err) {
    console.error(err);
    toast.error("Failed to update");
  } finally {
    setLoadingAction(false);
  }
};

const confirmDeleteTarget = (type: "folder" | "practice" | "folderPractice", data: any) => {
  setDeleteTarget({ type, data });
  setIsDeleteConfirmOpen(true);
};

const handleDeleteTarget = async () => {
  if (!deleteTarget) return;
  const { type, data } = deleteTarget;

  try {
    setLoadingAction(true);
    const selectedSwimmerId = localStorage.getItem("selected_swimmer_id");
    const swimmerId = selectedSwimmerId
      ? Number(selectedSwimmerId)
      : Number(localStorage.getItem("current_user_swimmer_id")) || 0;

    if (type === "folder") {
      const folderRepo = new FolderRepository();
      const folderId = Number(data.folder_id ?? data.id ?? 0);
      await folderRepo.deleteFolder(folderId);
    } else if (type === "practice") {
      const practiceRepo = new PracticeRepository();
      const practiceId = String(
        data.logicalPracticeId ?? data.practice_id ?? data.practiceId ?? data.id ?? ""
      ).trim();
      await practiceRepo.delete(practiceId, swimmerId);
    } else if (type === "folderPractice") {
      const folderRepo = new FolderRepository();
      const folderPracticeModelId = String(
        data.folder_practice_model_id ?? data.folderPracticeModelId ?? data.id ?? ""
      ).trim();
      await folderRepo.deleteFolderPractice(folderPracticeModelId);
    }

    toast.success("Deleted");
    setIsDeleteConfirmOpen(false);
    setDeleteTarget(null);
    await loadAllPractices();
  } catch (err) {
    console.error(err);
    toast.error("Failed to delete");
  } finally {
    setLoadingAction(false);
  }
};



  const handleConfirmDelete = (set: SetItem) => {
    setSetToDelete(set);
    setIsDeleteDialogOpen(true);
  };

  const removeSet = async () => {
    if (!setToDelete) return;

    try {

        // Inside removeSet
        if (!localPracticeId) {

          // Just remove locally
          setSets((prev) => {
            const updated = normalizeSets(prev.filter((s) => s.id !== setToDelete.id));
            isManuallyModifiedRef.current = true;
            
            // Update localStorage if this is a draft practice
            const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
            if (selectedPractice.isDraft) {
              const updatedPractice = {
                ...selectedPractice,
                sets: updated,
              };
              localStorage.setItem("selected_practice", JSON.stringify(updatedPractice));
            }
            
            return updated;
          });
          setIsDeleteDialogOpen(false);
          setSetToDelete(null);
          return;
        }

      const repo = new PracticeRepository();
      const swimmerId =
        parseInt(localStorage.getItem("selected_swimmer_id") || "0") ||
        parseInt(localStorage.getItem("current_user_swimmer_id") || "0");

      const response = await repo.removePracticeLap(
        Number(practiceId),
        Number(setToDelete.id),
        swimmerId
      );

      if (response.status === 1) {
        setSets((prev) => {
          const updated = normalizeSets(prev.filter((s) => s.id !== setToDelete.id));
          isManuallyModifiedRef.current = true;
          
          // Update localStorage if this is a draft practice
          const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
          if (selectedPractice.isDraft) {
            const updatedPractice = {
              ...selectedPractice,
              sets: updated,
            };
            localStorage.setItem("selected_practice", JSON.stringify(updatedPractice));
          }
          
          return updated;
        });
        if (sets.length <= 1) 
          setNoLaps(true);
      }    
      else {
        console.error(response.msg || "Failed to remove lap");
      }
    } catch (error) {
      console.error("Error removing lap:", error);
    } finally {
      setIsDeleteDialogOpen(false);
      setSetToDelete(null);
    }
  };

  // Power state - reserved for future power toggle functionality
  // const [isPowerOn, setIsPowerOn] = useState(false);
  const [isConnected, setIsConnected] = useState(bluetoothHelper.isConnected());
  const [isConnectDialogOpen, setIsConnectDialogOpen] = useState(false);

  // Note: handlePowerOnOff is available for future use with power toggle functionality
  // const handlePowerOnOff = async () => {
  //   try {
  //     if (!bluetoothHelper.isConnected()) {
  //       setIsConnectDialogOpen(true);
  //       return;
  //     }
  //     const newPowerState = !isPowerOn;
  //     setIsPowerOn(newPowerState);
  //     if (newPowerState) {
  //       await bluetoothHelper.sendString("PACE");
  //       toast.success("Power ON — Clock started & synced");
  //     } else {
  //       await bluetoothHelper.sendString("OFF");
  //       toast.warning("Power OFF — Clock stopped");
  //     }
  //   } catch (err) {
  //     console.error("Power toggle failed:", err);
  //     toast.error("Failed to toggle power");
  //   }
  // };

  // Clock View state

  const [isClockViewOpen, setIsClockViewOpen] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRunning) {
      interval = setInterval(() => setTimer((prev) => prev + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning]);

  const handleStartPractice = async () => {
    try {
      if (!isConnected) {
        const result = await bluetoothHelper.connect();
        setIsConnected(true);
        toast.success(`Connected to ${result.name}`);
      }

      setIsStarting(true);

      setTimer(0);
      await bluetoothHelper.sendString("RESET");

      await new Promise((resolve) => setTimeout(resolve, 200));

      // Start both together
      await bluetoothHelper.sendString("START");
      setIsRunning(true);
      setIsPaused(false);
      toast.success("Practice started — synced to device");

    } catch (error: any) {
      console.error("Failed to start practice:", error);
      toast.error("Unable to start practice");
    } finally {
      setIsStarting(false);
    }
  };

  const handlePausePractice = async () => {
    try {
      await bluetoothHelper.sendString("PAUSE");
      setIsRunning(false);
      setIsPaused(true);
      toast.warning("Practice paused — synced to device");
    } catch (error: any) {
      console.error("Pause failed:", error);
      toast.error("Failed to pause practice");
    }
  };

  const handleResumePractice = async () => {
    try {
      await bluetoothHelper.sendString("RESUME");
      setIsRunning(true);
      setIsPaused(false);
      toast.success("Resumed — synced to device");
    } catch (error: any) {
      console.error("Resume failed:", error);
      toast.error("Failed to resume practice");
    }
  };

  const handleResetPractice = async () => {
    try {
      await bluetoothHelper.sendString("RESET");
      setTimer(0);
      setIsRunning(false);
      setIsPaused(false);
      toast.info("Timer reset — synced to device");
    } catch (error: any) {
      console.error("Reset failed:", error);
      toast.error("Failed to reset timer");
    }
  };

  const handleFinishPractice = async () => {
    try {
      await bluetoothHelper.sendString("STOP");
      setIsRunning(false);
      setIsPaused(false);
      setTimer(0);
      toast.success("Practice finished — synced to device");
      setIsClockViewOpen(false);
    } catch (error: any) {
      console.error("Finish failed:", error);
      toast.error("Failed to finish practice");
    }
  };

  useEffect(() => {
    const handleClockConnection = async () => {
      if (isClockViewOpen) {
        try {
          if (!isConnected) {
            const result = await bluetoothHelper.connect();
            toast.success(`Connected to ${result.name}`);
            setIsConnected(true);

            // Reset device clock when connected
            await bluetoothHelper.sendString("RESET");
            console.log("Clock ready and reset");
          }
        } catch (err) {
          console.error("Failed to connect clock:", err);
          toast.error("Failed to connect to device");
        }
      } else {
        // Clock closed, disconnect
        if (isConnected) {
          await bluetoothHelper.sendString("OFF");
          setIsConnected(false);
          setIsRunning(false);
          setTimer(0);
          // setIsPowerOn(false); // Reserved for future power toggle functionality
          toast.info("Clock disconnected");
          console.log("Clock disconnected");
        }
      }
    };

    handleClockConnection();
  }, [isClockViewOpen]);

  const generatePracticePDF = async () => {
    const element = document.getElementById("practice-print-area");
    if (!element) return null;

    const html = `
      <html>
        <head>
          <title>Practice PDF</title>
        </head>
        <body>${element.innerHTML}</body>
      </html>
    `;

    const blob = new Blob([html], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    return url;
  };

  const [isAddPostDialogOpen, setIsAddPostDialogOpen] = useState(false);

  const handleSubmitPracticeFeedPost = async ({ text, mediaFile }: any) => {
    try {
      setLoadingAction(true);

      const postRepo = new PostRepository();

      let postPhoto = "";
      let postVideo = "";

      if (mediaFile) {
        if (mediaFile.type.startsWith("image/")) {
          const imgResp = await postRepo.uploadFeedImage(mediaFile);
          if (imgResp.status === 1) postPhoto = imgResp.image_link || "";
        } else if (mediaFile.type.startsWith("video/")) {
          const vidResp = await postRepo.uploadFeedVideo(mediaFile);
          if (vidResp.status === 1) postVideo = vidResp.video_link || "";
        }
      }

      const userId =
        Number(localStorage.getItem("current_user_swimmer_id")) ||
        Number(localStorage.getItem("selected_swimmer_id")) ||
        0;

      const coachUserId = Number(localStorage.getItem("coach_id")) || 0;

      const practiceImageData = "";
      const selectedPractice = JSON.parse(
        localStorage.getItem("selected_practice") || "{}"
      );
      const resolvedPracticeId = String(
        selectedPractice.practiceId ??
          selectedPractice.practice_id ??
          selectedPractice.id ??
          localPracticeId ??
          practiceId ??
          0
      );
      const resolvedPracticeDate = String(
        selectedPractice.practice_date ??
          selectedPractice.PracticeDate ??
          selectedPractice.date ??
          practiceDate ??
          new Date().toLocaleString("sv-SE", { timeZone: "UTC" }).replace("T", " ")
      );
      const resolvedPracticeTime = String(
        selectedPractice.practice_time ??
          selectedPractice.practiceTime ??
          practiceTime ??
          "AM"
      );
      const resolvedCourseTypeLookupId = Number(
        selectedPractice.courseTypeLookupId ??
          selectedPractice.CourseTypeLookupId ??
          courseTypeLookupId ??
          1
      ) || 1;
      const practiceData = {
        practice_id: resolvedPracticeId,
        practice_date: toSqlDateTime(resolvedPracticeDate),
        practice_time: resolvedPracticeTime,
        CourseTypeLookupId: resolvedCourseTypeLookupId,
        createdAt: toSqlDateTime(selectedPractice.createdAt),
      };

      const lapTimestamp = toSqlDateTime(undefined);
      const lapsData = sets.map((s) => ({
        id: 0,
        practice_lap_id: 0,
        practice_id: resolvedPracticeId,
        rep: String(s.reps ?? 0),
        distance: String(s.distance ?? 0),
        interval: String((s.minutes || 0) * 60 + (s.seconds || 0)),
        energy_zone: s.energy_zone || "Warm Up",
        description: s.description || "",
        time: 0,
        createdAt: lapTimestamp,
        updated_at: lapTimestamp,
        passedRepNumber: 0,
        isNoEditable: false,
        isPassed: 0,
        user_id: String(userId),
        coach_id: String(coachUserId),
      }));

      console.log("[SharePracticeFeed] practice payload", practiceData);
      console.log("[SharePracticeFeed] laps payload", lapsData);

      const result = await postRepo.sharePracticeFeed(
        practiceData,
        practiceImageData,
        lapsData,
        text || "",
        postPhoto || "",
        postVideo || "",
        userId,
        coachUserId
      );

      if (result.status === 1) {
        toast.success("Practice posted to feed successfully");
      } else {
        toast.error(result.msg || "Failed to post practice to feed");
      }

      setIsAddPostDialogOpen(false);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setLoadingAction(false);
    }
  };

  const [isFavoriteActionDialogOpen, setIsFavoriteActionDialogOpen] = useState(false);
  const [isFavoriteFormDialogOpen, setIsFavoriteFormDialogOpen] = useState(false);
  const [isCreateFavoriteFolderDialogOpen, setIsCreateFavoriteFolderDialogOpen] = useState(false);
  const [favoriteFolderName, setFavoriteFolderName] = useState("");
  const [favoriteFolderDescription, setFavoriteFolderDescription] = useState("");
  const [selectedFavoritePracticeId, setSelectedFavoritePracticeId] = useState("");
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<{
    type: "folder" | "practice" | "folderPractice";
    data: any;
  } | null>(null);
  const [renameName, setRenameName] = useState("");
  const [renameDescription, setRenameDescription] = useState("");
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "folder" | "practice" | "folderPractice";
    data: any;
  } | null>(null);

  const handleTwoDigitNumber = (
        value: string,
        setter: React.Dispatch<React.SetStateAction<string>>
      ) => {
        const digitsOnly = value.replace(/\D/g, "");
        let twoDigits = digitsOnly.slice(0, 2);

        if (twoDigits === "") {
          setter("");
          return;
        }

        const num = Number(twoDigits);

        if (num > 59) {
          twoDigits = "59";
        }

        setter(twoDigits);
      };


  const [isFromFavoriteTemplate, setIsFromFavoriteTemplate] = useState(false);
  const selectedPracticeImage = resolveSelectedPracticeImage();
  const listPracticeImage = (() => {
    const targetId = String(localPracticeId ?? practiceId ?? "").trim();
    if (!targetId) return null;
    const match = allPractices.find((p: any) => {
      const candidateId = String(
        p?.logicalPracticeId ?? p?.practiceId ?? p?.practice_id ?? p?.id ?? ""
      ).trim();
      return candidateId === targetId;
    });
    const raw = match?.imageData ?? (match as any)?.practice_imageData ?? "";
    const normalized =
      typeof raw === "string" ? raw.trim().replace(/\\\//g, "/") : "";
    if (!normalized || normalized.toLowerCase() === "null") return null;
    return normalized;
  })();
  const resolvedPracticeImage =
    practiceImageUrl ?? practiceImagePreview ?? selectedPracticeImage ?? listPracticeImage;


  return (
  <div className="space-y-6">
        {loadingAction && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-900 px-6 py-4 rounded-lg shadow-lg text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3"></div>
        <p className="text-sm text-muted-foreground">Processing...</p>
        </div>
        </div>
        )}

        {loadingAction && (
          <div className="fixed inset-0 z-40 bg-transparent pointer-events-auto"></div>
        )}

        <Dialog open={aiErrorDialogOpen} onOpenChange={setAiErrorDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>AI Image Error</DialogTitle>
              <DialogDescription>
                {aiErrorMessage || "Unable to process the uploaded image."}
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end mt-4">
              <Button onClick={() => setAiErrorDialogOpen(false)}>OK</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            {/* <h1 className="text-3xl mb-2">Training Sets</h1> */}
            <h1 className="text-3xl mb-2">
              {practiceId ? "Training Sets" : "Create a Set"}
            </h1>

            <p className="text-muted-foreground">
              Build and customize your workout sets
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {practiceId ? (
              <>
                <input
                  id="practice-image-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handlePracticeImageChange(e.target.files?.[0] ?? null)}
                />
                <label
                  htmlFor="practice-image-upload"
                  className="cursor-pointer rounded-md border bg-muted/30 hover:bg-muted/50 transition-colors overflow-hidden"
                  title="Click to upload practice image"
                >
                  {resolvedPracticeImage ? (
                    <img
                      src={resolvedPracticeImage}
                      alt="Practice"
                      className="h-16 w-16 object-cover"
                    />
                  ) : (
                    <div className="h-16 w-16 flex items-center justify-center text-[10px] text-muted-foreground px-1 text-center">
                      Upload Image
                    </div>
                  )}
                </label>
              </>
            ) : (
              <>
                <input
                  id="ai-image-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleAiImageChange(e.target.files?.[0] ?? null)}
                />
                <label
                  htmlFor="ai-image-upload"
                  className="cursor-pointer rounded-md border bg-muted/30 hover:bg-muted/50 transition-colors overflow-hidden"
                  title="Click to upload image for AI"
                >
                  {aiImagePreview ? (
                    <img
                      src={aiImagePreview}
                      alt="AI Preview"
                      className="h-16 w-16 object-cover"
                    />
                  ) : (
                    <div className="h-16 w-16 flex items-center justify-center text-[10px] text-muted-foreground px-1 text-center">
                       Image
                    </div>
                  )}
                </label>
              </>
            )}
            {!noLaps && sets.length > 0 && (
              <Badge variant="secondary" className="px-3 py-1 flex items-center">
                <Timer className="mr-1 h-4 w-4" />
                Total: {calculateTotalTime()}
              </Badge>
            )}
          </div>
        </div>

        

        {/* Top-Right Icon Actions */}
        <TooltipProvider>
          <div className="flex justify-end gap-3 mt-2">

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => setIsAddSetDialogOpen(true)}
                  disabled={loadingAction}
                >
                  <Plus className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Add Set</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  onClick={() => setIsLadderDialogOpen(true)}
                  disabled={loadingAction}
                >
                  <TrendingUp className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Add Ladder</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={() => setIsShareDialogOpen(true)}
                  disabled={loadingAction}
                >
                  <Share2 className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Share</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => setIsFavoriteActionDialogOpen(true)}
                  disabled={loadingAction}
                >
                  <Heart className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Favorite</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => onSectionChange?.("practiceclock", { sets, practiceId, name })}
                  disabled={loadingAction}
                >
                  <Timer className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Start Practice</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleSaveLaps}
                  disabled={loadingAction}
                >
                  <Save className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Save</TooltipContent>
            </Tooltip>

          </div>
        </TooltipProvider>

        <div className="grid gap-6">
          {/* Main Sets Area */}
          <div className="lg:col-span-3">
            <SetList
              sets={sets}
              energyZones={energyZones}
              onUpdate={updateSet}
              onDelete={handleConfirmDelete}
              onAddSet={addDefaultSet}
              showAddButton={!practiceId}
              onCopySet={handleCopySet}
              onMoveUp={(id) => moveSetById(id, "up")}
              onMoveDown={(id) => moveSetById(id, "down")}
              calculateTotalTime={calculateTotalTime}
              calculateTotalDistance={calculateTotalDistance}
            />
          </div>
        </div>

        <div id="practice-print-area" className="hidden print:block">

          <h1 className="text-2xl font-bold mb-4">Practice Summary</h1>

          <p><strong>Name:</strong> {name}</p>
          <p><strong>Description:</strong> {description}</p>

          <h2 className="text-xl font-semibold mt-4 mb-3">Practice Sets</h2>

          <table className="w-full border-collapse print-table">
            <thead>
              <tr>
                <th className="border px-3 py-2 text-left">Set</th>
                <th className="border px-3 py-2 text-left">Reps</th>
                <th className="border px-3 py-2 text-left">Distance</th>
                <th className="border px-3 py-2 text-left">Interval</th>
                <th className="border px-3 py-2 text-left">Energy Zone</th>
                <th className="border px-3 py-2 text-left">Description</th>
              </tr>
            </thead>
            <tbody>
              {sets.map((s, i) => (
                <tr key={i}>
                  <td className="border px-3 py-2">Set {s.number}</td>
                  <td className="border px-3 py-2">{s.reps}</td>
                  <td className="border px-3 py-2">{s.distance}</td>
                  <td className="border px-3 py-2">
                    {s.minutes}:{s.seconds.toString().padStart(2, "0")}
                  </td>
                  <td className="border px-3 py-2">{s.energy_zone}</td>
                  <td className="border px-3 py-2">{s.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DeleteSetDialog
          isOpen={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          onConfirm={removeSet}
        />

        <PracticeClockDialog
          isOpen={isClockViewOpen}
          onOpenChange={setIsClockViewOpen}
          timer={timer}
          isRunning={isRunning}
          isPaused={isPaused}
          isStarting={isStarting}
          totalRounds={sets.length}
          totalReps={calculateTotalReps()}
          totalDistance={calculateTotalDistance()}
          totalSets={sets.length}
          onStart={handleStartPractice}
          onPause={handlePausePractice}
          onResume={handleResumePractice}
          onReset={handleResetPractice}
          onFinish={handleFinishPractice}
        />

        <Dialog open={isFavoriteActionDialogOpen} onOpenChange={setIsFavoriteActionDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Favorite Sets</DialogTitle>
              {/* <DialogDescription>Select how you want to proceed.</DialogDescription> */}
            </DialogHeader>

            <div className="flex flex-col space-y-3 mt-4">
                  <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setIsFavoriteActionDialogOpen(false);
                        loadAllPractices();
                      }}
                    >
                      Open
                    </Button>

              <Button
                onClick={() => {
                  if (!ensureSetsExist()) return;
                  setIsFavoriteActionDialogOpen(false);
                  setIsFavoriteFormDialogOpen(true);
                }}
              variant="outline"
                className="w-full"
              >
                Save
            </Button>

              <Button
                onClick={() => {
                  if (!ensureSetsExist()) return;
                  const selectedPractice = JSON.parse(
                    localStorage.getItem("selected_practice") || "{}"
                  );
                  
                  // Use existing draft ID if this is an update, otherwise create new
                  const draftId = selectedPractice.isDraft 
                    ? selectedPractice.id 
                    : `draft-${Date.now()}`;

                  const toNumber = (v: number | "") => (typeof v === "number" ? v : 0);

                  const draftData = {
                    id: draftId,
                    name: name || "Untitled Draft",
                    description: description || "",
                    date: new Date().toLocaleDateString("en-US", {
                      month: "2-digit",
                      day: "2-digit",
                      year: "numeric",
                    }),
                    type: "draft" as const,
                    yardage: sets.reduce(
                      (sum, set) => sum + toNumber(set.reps) * toNumber(set.distance),
                      0
                    ),
                    practice_time: "",
                    sets,
                    createdAt: new Date().toISOString(),
                  };


                  const existingDrafts = JSON.parse(
                    localStorage.getItem("practice_drafts") || "[]"
                  );
                  
                  // Remove old draft if updating existing one
                  const filteredDrafts = existingDrafts.filter(
                    (d: any) => d.id !== draftId
                  );
                  
                  const updatedDrafts = [...filteredDrafts, draftData];
                  localStorage.setItem("practice_drafts", JSON.stringify(updatedDrafts));
                  
                  setIsFavoriteActionDialogOpen(false);
                  toast.success("Draft saved");
                  
                  // Update selected practice to mark as draft
                  localStorage.setItem(
                    "selected_practice",
                    JSON.stringify({
                      ...selectedPractice,
                      isDraft: true,
                      sets: sets,
                    })
                  );
                }}
                variant="outline"
                className="w-full"
              >
                Save as Draft
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={isFavoriteFormDialogOpen} onOpenChange={setIsFavoriteFormDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save as Favorite</DialogTitle>
              <DialogDescription>Update the name and description before saving.</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">

              <div>
                <label className="block text-sm mb-1">Name</label>
                <input
                  className="w-full border rounded p-2"
                  value={newPracticeName}
                  onChange={(e) => setNewPracticeName(e.target.value)}
                  maxLength={MAX_PRACTICE_NAME_LENGTH}
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Description</label>
                <textarea
                  className="w-full border rounded p-2"
                  value={newPracticeDescription}
                  onChange={(e) => setNewPracticeDescription(e.target.value)}
                  maxLength={MAX_PRACTICE_DESCRIPTION_LENGTH}
                />
              </div>

              <Button
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                onClick={async () => {
                  if (!ensureSetsExist()) return;
                      const resolvedName = newPracticeName || name || "";
                      const resolvedDescription = newPracticeDescription || description || "";
                      if (!validatePracticeMetadata(resolvedName, resolvedDescription)) {
                        return;
                      }
                      setIsFavoriteFormDialogOpen(false);
                      setLoadingAction(true);
                            
                      try {
                        if (!localPracticeId) {
                          const newPracticeId = Math.floor(10000 + Math.random() * 90000);
                          const resolvedFolderId = resolveFolderId();

                          await savePracticeAndLaps(
                            newPracticeId,
                            resolvedName,
                            resolvedDescription,
                            true,
                            resolvedFolderId
                          );

                          setLocalPracticeId(newPracticeId);
                          setIsFavorite(true);
                          toast.success("Practice saved as favorite");
                          onSectionChange?.("practice");
                          return;
                        }

                        await savePracticeAndLaps(
                          localPracticeId,
                          resolvedName,
                          resolvedDescription,
                          true
                        );

                        setIsFavorite(true);
                        toast.success("Practice saved as favorite");
                        onSectionChange?.("practice");
                      } catch {
                        toast.error("Failed to save favorite");
                      } finally {
                        setLoadingAction(false);
                      }
                    }}>

                Save Favorite
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={isConnectDialogOpen} onOpenChange={setIsConnectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Clock Not Connected</DialogTitle>
              <DialogDescription>
                Please connect your Swimnerd Clock first using the{" "}
                <b>Tools</b> page before toggling power.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end mt-4">
              <Button onClick={() => setIsConnectDialogOpen(false)} variant="default">
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Share Practice</DialogTitle>
              <DialogDescription>Select how you want to share this practice.</DialogDescription>
            </DialogHeader>

            <div className="flex flex-col space-y-3 mt-4">

              <Button
          variant="default"
          onClick={async () => {
            window.print();
          }}
        >
          Print Practice
        </Button>

              <Button
          variant="secondary"
          onClick={async () => {
            const pdfUrl = await generatePracticePDF();
            if (!pdfUrl) return;

            const subject = encodeURIComponent(name || "Training Practice");
            const from = encodeURIComponent(
              secureStorage.get("current_user_email") || ""
            );

            const body = encodeURIComponent(
              `Your practice PDF is attached as a link:\n\n${pdfUrl}\n\nDownload and attach manually if needed.`
            );

            window.location.href =
              `https://mail.google.com/mail/?view=cm&fs=1&from=${from}&su=${subject}&body=${body}`;
          }}
        >
          Email Practice
        </Button>

        <Button
        variant="outline"
        onClick={() => {
        setIsShareDialogOpen(false);
        setIsAddPostDialogOpen(true);
        }}
        >
        Share to Feed
        </Button>

            
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={isLadderDialogOpen}
          onOpenChange={(open) => {
            setIsLadderDialogOpen(open);
            if (!open) resetLadderForm();
          }}
        >

          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Ladder</DialogTitle>
            </DialogHeader>

              {ladderFormError && (
                <p className="text-sm text-red-600 font-medium">
                  {ladderFormError}
                </p>
              )}


            <div className="space-y-4 mt-4">

              <div className="flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[140px]">
                  <label className="block text-sm">Rounds</label>
                  <input
                    type="number"
                    className="w-full border p-2 rounded"
                    value={ladderRounds}
                    onChange={(e) => setLadderRounds(e.target.value)}
                  />
                </div>

                <div className="w-full sm:w-[220px] sm:ml-auto">
                  <label className="block text-sm">Order</label>
                  <ToggleGroup
                    type="single"
                    value={ladderDirection}
                    onValueChange={(value) => {
                      if (value) setLadderDirection(value as "asc" | "desc");
                    }}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <ToggleGroupItem value="asc" className="flex-1">
                      Ascend
                    </ToggleGroupItem>
                    <ToggleGroupItem value="desc" className="flex-1">
                      Descend
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
              </div>

              <div>
                <label className="block text-sm">Reps (Max: {MAX_REPS})</label>
                <input
                  type="number"
                  className="w-full border p-2 rounded"
                  value={ladderReps}
                  min="1"
                  max={MAX_REPS}
                  onChange={(e) => {
                    const value = e.target.value;
                    const num = parseInt(value) || 0;
                    if (value === '' || (num >= 1 && num <= MAX_REPS)) {
                      setLadderReps(value);
                    } else if (num > MAX_REPS) {
                      setLadderReps(MAX_REPS.toString());
                      toast.error(`Reps cannot exceed ${MAX_REPS}`);
                    }
                  }}
                />
              </div>

              <div>
                <label className="block text-sm">Distance (Max: {MAX_DISTANCE}m)</label>
                <input
                  type="number"
                  className="w-full border p-2 rounded"
                  value={ladderDistance}
                  min="1"
                  max={MAX_DISTANCE}
                  onChange={(e) => {
                    const value = e.target.value;
                    const num = parseInt(value) || 0;
                    if (value === '' || (num >= 1 && num <= MAX_DISTANCE)) {
                      setLadderDistance(value);
                    } else if (num > MAX_DISTANCE) {
                      setLadderDistance(MAX_DISTANCE.toString());
                      toast.error(`Distance cannot exceed ${MAX_DISTANCE} meters`);
                    }
                  }}
                />
              </div>

              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-sm">Minutes</label>
                  <input
                      type="text"
                      inputMode="numeric"
                      maxLength={2}
                      className="w-full border p-2 rounded"
                      value={ladderMinutes}
                      onChange={(e) =>
                        handleTwoDigitNumber(e.target.value, setLadderMinutes)
                      }
                    />

                </div>

                <div className="flex-1">
                  <label className="block text-sm">Seconds</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    className="w-full border p-2 rounded"
                    value={ladderSeconds}
                    onChange={(e) =>
                      handleTwoDigitNumber(e.target.value, setLadderSeconds)
                    }
                  />

                </div>
              </div>

              <div>
                <label className="block text-sm">Energy Zone</label>
                <select
                  className="w-full border p-2 rounded"
                  value={ladderEnergy}
                  onChange={(e) => setLadderEnergy(e.target.value)}
                >
                  {energyZones.map((zone) => (
                    <option key={zone} value={zone}>{zone}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm">Description</label>
                <input
                  className="w-full border p-2 rounded"
                  value={ladderDescription}
                  onChange={(e) => setLadderDescription(e.target.value)}
                />
              </div>

              <Button
                className="w-full bg-blue-600 text-white"
                onClick={() => {
                  const hasError = hasNegativeValue(
                    ladderRounds,
                    ladderReps,
                    ladderDistance,
                    ladderMinutes,
                    ladderSeconds
                  );

                  if (hasError) {
                    setLadderFormError("Negative values are not allowed");
                    return;
                  }

                  setLadderFormError(null);
                  createLadderSets();
                  resetLadderForm();
                  setIsLadderDialogOpen(false);
                }}
              >
                Add Ladder
              </Button>

            </div>
          </DialogContent>
        </Dialog>

        <Dialog
            open={isAddSetDialogOpen}
            onOpenChange={(open) => {
              setIsAddSetDialogOpen(open);
              if (!open) resetSingleSetForm();
            }}
          >
          <DialogContent className="max-h-[90vh] overflow-hidden sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add Split</DialogTitle>
            </DialogHeader>


            {singleFormError && (
              <p className="text-sm text-red-600 font-medium">
                {singleFormError}
              </p>
            )}

            <div className="mt-4 max-h-[70vh] space-y-4 overflow-y-auto pr-2 pb-4">
              <div>
                <label className="block text-sm">Rounds</label>
                <input
                  type="number"
                  className="w-full border p-2 rounded"
                  value={singleRounds}
                  min="1"
                  onChange={(e) => setSingleRounds(e.target.value)}
                />
              </div>

              <div className="space-y-4">
                {splitSetDrafts.map((draft, index) => (
                  <div key={draft.id} className="rounded-lg border p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">Set {index + 1}</p>
                      {splitSetDrafts.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeSplitSetDraft(draft.id)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm">Reps (Max: {MAX_REPS})</label>
                      <input
                        type="number"
                        className="w-full border p-2 rounded"
                        value={draft.reps}
                        min="1"
                        max={MAX_REPS}
                        onChange={(e) => {
                          const value = e.target.value;
                          const num = parseInt(value) || 0;
                          if (value === "" || (num >= 1 && num <= MAX_REPS)) {
                            updateSplitSetDraft(draft.id, "reps", value);
                          } else if (num > MAX_REPS) {
                            updateSplitSetDraft(draft.id, "reps", MAX_REPS.toString());
                            toast.error(`Reps cannot exceed ${MAX_REPS}`);
                          }
                        }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm">Distance (Max: {MAX_DISTANCE}m)</label>
                      <input
                        type="number"
                        className="w-full border p-2 rounded"
                        value={draft.distance}
                        min="1"
                        max={MAX_DISTANCE}
                        onChange={(e) => {
                          const value = e.target.value;
                          const num = parseInt(value) || 0;
                          if (value === "" || (num >= 1 && num <= MAX_DISTANCE)) {
                            updateSplitSetDraft(draft.id, "distance", value);
                          } else if (num > MAX_DISTANCE) {
                            updateSplitSetDraft(draft.id, "distance", MAX_DISTANCE.toString());
                            toast.error(`Distance cannot exceed ${MAX_DISTANCE} meters`);
                          }
                        }}
                      />
                    </div>

                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="block text-sm">Minutes</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={2}
                          className="w-full border p-2 rounded"
                          value={draft.minutes}
                          onChange={(e) => {
                            const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 2);
                            updateSplitSetDraft(draft.id, "minutes", digitsOnly);
                          }}
                        />
                      </div>

                      <div className="flex-1">
                        <label className="block text-sm">Seconds</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={2}
                          className="w-full border p-2 rounded"
                          value={draft.seconds}
                          onChange={(e) => {
                            const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 2);
                            const safeValue =
                              digitsOnly === ""
                                ? ""
                                : String(Math.min(Number(digitsOnly), 59));
                            updateSplitSetDraft(draft.id, "seconds", safeValue);
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm">Energy Zone</label>
                      <select
                        className="w-full border p-2 rounded"
                        value={draft.energy_zone}
                        onChange={(e) =>
                          updateSplitSetDraft(draft.id, "energy_zone", e.target.value)
                        }
                      >
                        {energyZones.map((zone) => (
                          <option key={zone} value={zone}>{zone}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm">Description</label>
                      <input
                        className="w-full border p-2 rounded"
                        value={draft.description}
                        onChange={(e) =>
                          updateSplitSetDraft(draft.id, "description", e.target.value)
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>

            </div>

            <div className="sticky bottom-0 z-10 -mx-6 border-t bg-transparent px-4 py-2">
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                  onClick={addSplitSetDraft}
                >
                  + Set
                </Button>

                <Button
                  className="flex-1 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                  onClick={() => {
                    const hasError = splitSetDrafts.some((draft) =>
                      hasNegativeValue(
                        singleRounds,
                        draft.reps,
                        draft.distance,
                        draft.minutes,
                        draft.seconds
                      )
                    );

                    if (hasError) {
                      setSingleFormError("Negative values are not allowed");
                      return;
                    }

                    setSingleFormError(null);
                    createSingleSet();
                    setIsAddSetDialogOpen(false);
                  }}
                >
                  Add
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={isEmptySetsDialogOpen}
          onOpenChange={setIsEmptySetsDialogOpen}
        >
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>No Sets Added</DialogTitle>
              <DialogDescription>
                Please add at least one set or ladder before saving the practice.
              </DialogDescription>
            </DialogHeader>

            <div className="flex justify-end mt-4">
              <Button onClick={() => setIsEmptySetsDialogOpen(false)}>
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <CreatePracticeDialog
            isOpen={isNewPracticeDialogOpen}
            onOpenChange={handleNewPracticeDialogOpenChange}
            practiceName={newPracticeName}
            practiceDescription={newPracticeDescription}
            onNameChange={setNewPracticeName}
            onDescriptionChange={setNewPracticeDescription}
            onSave={async () => {

              // Guard: must have sets
              if (!sets || sets.length === 0) {
                setIsEmptySetsDialogOpen(true);
                return;
              }

              if (!validatePracticeMetadata(newPracticeName, newPracticeDescription)) {
                return;
              }

              setLoadingAction(true);

              try {
                  const newPracticeId = Math.floor(10000 + Math.random() * 90000);
                  const resolvedFolderId = resolveFolderId();

                  await savePracticeAndLaps(
                    newPracticeId,
                    newPracticeName,
                    newPracticeDescription,
                    pendingFavorite ?? isFavorite,
                    resolvedFolderId,
                    practiceImageFile
                  );

                  setLocalPracticeId(newPracticeId);
                  setPracticeImageFile(null);

                if (pendingFavorite !== null) {
                  setIsFavorite(pendingFavorite);
                  setPendingFavorite(null);
                }

                // Close dialog FIRST
                setIsNewPracticeDialogOpen(false);

                // Then redirect
                onSectionChange?.("practice");

                if (!isCreatingFromFavorite) {
                  onClose?.();
                }

                setIsCreatingFromFavorite(false);


                toast.success("Practice created successfully");

              } catch (err) {
                console.error(err);
                toast.error("Failed to create practice");
              } finally {
                setLoadingAction(false);
              }
            }}
          />

        <Dialog
          open={isOpenPracticeDialogOpen}
          onOpenChange={setIsOpenPracticeDialogOpen}
        >
  <DialogContent className="max-w-2xl">
    <DialogHeader>
      <DialogTitle>Select Practice</DialogTitle>
      <DialogDescription>
        Choose a practice to open
      </DialogDescription>
    </DialogHeader>

    {loadingPractices ? (
      <p className="text-sm text-muted-foreground">Loading practices...</p>
    ) : (
      <div className="space-y-4 max-h-[400px] overflow-y-auto">
        {!activeFavoriteFolderId && (
          <>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-muted-foreground">Favorite Folders</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsCreateFavoriteFolderDialogOpen(true)}
                >
                  + Create Folder
                </Button>
              </div>
              {favoriteFolders.length === 0 ? (
                <p className="text-sm text-muted-foreground">No favorite folders found</p>
              ) : (
                <div className="space-y-2">
                  {favoriteFolders.map((folder: any) => (
                <div
                  key={folder.folder_id}
                  className="border rounded-lg p-3 cursor-pointer hover:bg-muted"
                  onClick={() => loadFavoriteFolderPractices(String(folder.folder_id))}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium">{folder.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {folder.description || "No description"}
                      </p>
                    </div>
                    {showManageActions && (
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          aria-label="Rename folder"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRenameDialog("folder", folder);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            aria-label="Delete folder"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDeleteTarget("folder", folder);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
            </div>

            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Favorite Practices</p>
              {favoriteRootPractices.length === 0 ? (
                <p className="text-sm text-muted-foreground">No practices found</p>
              ) : (
                <div className="space-y-3">
                {favoriteRootPractices.map((practice) => (
                  <div  
                    key={practice.id}
                    className="border rounded-lg p-3 cursor-pointer hover:bg-muted"
           
            onClick={() => {
                const targetIds = [
                  (practice as any).logicalPracticeId,
                  (practice as any).practice_id,
                  (practice as any).folder_practice_model_id,
                  (practice as any).practiceId,
                  practice.practiceId,
                  practice.id,
                ]
                  .filter(Boolean)
                  .map(String);

  const practiceLaps = allPracticeLaps.filter((lap) => {
    const lapIds = [
      lap.practice_id,
      (lap as any).practiceId,
      (lap as any).folder_practice_model_id,
    ]
      .filter(Boolean)
      .map(String);

    return lapIds.some((id) => targetIds.includes(id));
  });

  // Append laps each time a favorite is chosen
  setSets(prev => {
  const offset = prev.length;
  const newSets = practiceLaps.map((lap, index) => {
    const parsedDistance = parseInt(String(lap.distance));
    return {
      id: crypto.randomUUID(),
      number: offset + index + 1,
      reps: parseInt(String(lap.rep)) || 1,
      minutes: Math.floor(
        (parseInt(String(lap.interval_lap ?? (lap as any).interval ?? 0)) || 0) / 60
      ),
      seconds:
        (parseInt(String(lap.interval_lap ?? (lap as any).interval ?? 0)) || 0) % 60,
      distance: (!isNaN(parsedDistance) && lap.distance != null && lap.distance !== '') ? parsedDistance : 25,
      energy_zone: lap.energy_zone || "Warm Up",
      description: lap.description || `Lap ${offset + index + 1}`,
    };
  });

  const updatedSets = normalizeSets([...prev, ...newSets]);
  
  // Mark as manually modified to prevent useEffect from overwriting
  isManuallyModifiedRef.current = true;
  
  // Update localStorage if this is a draft practice
  const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
  if (selectedPractice.isDraft) {
    const updatedPractice = {
      ...selectedPractice,
      sets: updatedSets,
    };
    localStorage.setItem("selected_practice", JSON.stringify(updatedPractice));
  }
  
  return updatedSets;
});

setIsDirtySets(true);


  if (!practiceId) {
  setLocalPracticeId(undefined);
  setIsFromFavoriteTemplate(true);
} else {
  setLocalPracticeId(practiceId);
  setIsFromFavoriteTemplate(false);
}

  const targetPracticeId =
    (practice as any).logicalPracticeId ??
    (practice as any).practice_id ??
    (practice as any).practiceId ??
    practice.id;
  const selectedPracticePayload = {
    ...(practice as any),
    practiceId: targetPracticeId,
    id: targetPracticeId,
    imageData:
      (practice as any).imageData ??
      (practice as any).image_data ??
      (practice as any).practice_imageData ??
      (practice as any).practiceImageData ??
      null,
  };
  localStorage.setItem("selected_practice", JSON.stringify(selectedPracticePayload));


  onSectionChange?.("sets", {
    name: practice.name,
    description: practice.description,
  });

  setIsOpenPracticeDialogOpen(false);
}}



                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium">{practice.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {practice.description || "No description"}
                      </p>
                    </div>
                    {showManageActions && (
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          aria-label="Rename practice"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRenameDialog("practice", practice);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          aria-label="Delete practice"
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDeleteTarget("practice", practice);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
                </div>
              )}
            </div>
          </>
        )}

        {activeFavoriteFolderId && (
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">
              Folder Practices
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mb-2"
              onClick={() => {
                setActiveFavoriteFolderId(null);
                setFavoriteFolderPractices([]);
              }}
            >
              Back to Favorites
            </Button>
            {loadingFavoriteFolderPractices ? (
              <p className="text-sm text-muted-foreground">Loading folder practices...</p>
            ) : favoriteFolderPractices.length === 0 ? (
              <p className="text-sm text-muted-foreground">No practices found in folder</p>
            ) : (
              <div className="space-y-3">
                {favoriteFolderPractices.map((practice: any) => (
                  <div
                    key={practice.folder_practice_model_id ?? practice.id}
                    className="border rounded-lg p-3 cursor-pointer hover:bg-muted"
                    onClick={() => {
                      const targetIds = [
                        practice.folder_practice_model_id,
                        practice.folderPracticeModelId,
                        practice.practice_id,
                        practice.practiceId,
                        practice.id,
                      ]
                        .filter(Boolean)
                        .map(String);

                      const practiceLaps = allPracticeLaps.filter((lap) => {
                        const lapIds = [
                          lap.practice_id,
                          (lap as any).practiceId,
                          (lap as any).folder_practice_model_id,
                        ]
                          .filter(Boolean)
                          .map(String);

                        return lapIds.some((id) => targetIds.includes(id));
                      });

                      setSets((prev) => {
                        const offset = prev.length;
                        const newSets = practiceLaps.map((lap, index) => {
                          const parsedDistance = parseInt(String(lap.distance));
                          return {
                            id: crypto.randomUUID(),
                            number: offset + index + 1,
                            reps: parseInt(String(lap.rep)) || 1,
                            minutes: Math.floor(
                              (parseInt(String(lap.interval_lap ?? (lap as any).interval ?? 0)) || 0) / 60
                            ),
                            seconds:
                              (parseInt(String(lap.interval_lap ?? (lap as any).interval ?? 0)) || 0) % 60,
                            distance: (!isNaN(parsedDistance) && lap.distance != null && lap.distance !== '') ? parsedDistance : 25,
                            energy_zone: lap.energy_zone || "Warm Up",
                            description: lap.description || `Lap ${offset + index + 1}`,
                          };
                        });

                        const updatedSets = normalizeSets([...prev, ...newSets]);
                        isManuallyModifiedRef.current = true;

                        const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
                        if (selectedPractice.isDraft) {
                          const updatedPractice = {
                            ...selectedPractice,
                            sets: updatedSets,
                          };
                          localStorage.setItem("selected_practice", JSON.stringify(updatedPractice));
                        }

                        return updatedSets;
                      });

                      setIsDirtySets(true);

                      if (!practiceId) {
                        setLocalPracticeId(undefined);
                        setIsFromFavoriteTemplate(true);
                      } else {
                        setLocalPracticeId(practiceId);
                        setIsFromFavoriteTemplate(false);
                      }

                      onSectionChange?.("sets", {
                        name: practice.name,
                        description: practice.description,
                      });

                      setIsOpenPracticeDialogOpen(false);
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium">{practice.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {practice.description || "No description"}
                        </p>
                      </div>
                      {showManageActions && (
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            aria-label="Rename practice"
                            onClick={(e) => {
                              e.stopPropagation();
                              openRenameDialog("folderPractice", practice);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            aria-label="Delete practice"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDeleteTarget("folderPractice", practice);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    )}
  </DialogContent>
</Dialog>

        <Dialog
          open={isCreateFavoriteFolderDialogOpen}
          onOpenChange={(open) => {
            setIsCreateFavoriteFolderDialogOpen(open);
            if (!open) {
              setFavoriteFolderName("");
              setFavoriteFolderDescription("");
              setSelectedFavoritePracticeId("");
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Favorite Folder</DialogTitle>
              <DialogDescription>
                Create a favorite folder and move the selected practice into it.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm mb-1">Folder Name</label>
                <input
                  className="w-full border rounded p-2"
                  value={favoriteFolderName}
                  onChange={(e) => setFavoriteFolderName(e.target.value)}
                  maxLength={60}
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Description</label>
                <textarea
                  className="w-full border rounded p-2"
                  value={favoriteFolderDescription}
                  onChange={(e) => setFavoriteFolderDescription(e.target.value)}
                  maxLength={500}
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Practice to Move</label>
                <select
                  className="w-full border rounded p-2"
                  value={selectedFavoritePracticeId}
                  onChange={(e) => setSelectedFavoritePracticeId(e.target.value)}
                >
                  <option value="">Select a practice</option>
                  {favoriteRootPractices.map((p) => {
                      const id = String(
                        (p as any).logicalPracticeId ??
                          (p as any).practice_id ??
                          (p as any).practiceId ??
                          (p as any).id ??
                          ""
                      ).trim();
                      return (
                        <option key={id} value={id}>
                          {p.name}
                        </option>
                      );
                    })}
                </select>
              </div>

              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                onClick={createFavoriteFolderAndMovePractice}
              >
                Create Folder
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={isRenameDialogOpen}
          onOpenChange={(open) => {
            setIsRenameDialogOpen(open);
            if (!open) {
              setRenameTarget(null);
              setRenameName("");
              setRenameDescription("");
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Rename</DialogTitle>
              <DialogDescription>Update the name and description.</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm mb-1">Name</label>
                <input
                  className="w-full border rounded p-2"
                  value={renameName}
                  onChange={(e) => setRenameName(e.target.value)}
                  maxLength={MAX_PRACTICE_NAME_LENGTH}
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Description</label>
                <textarea
                  className="w-full border rounded p-2"
                  value={renameDescription}
                  onChange={(e) => setRenameDescription(e.target.value)}
                  maxLength={MAX_PRACTICE_DESCRIPTION_LENGTH}
                />
              </div>

              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                onClick={handleRenameSave}
              >
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={isDeleteConfirmOpen}
          onOpenChange={(open) => {
            setIsDeleteConfirmOpen(open);
            if (!open) {
              setDeleteTarget(null);
            }
          }}
        >
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Delete</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete {deleteTarget?.data?.name || "this item"}?
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDeleteTarget}
              >
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>


        <AddPostDialog
          open={isAddPostDialogOpen}
          onOpenChange={setIsAddPostDialogOpen}
          onSubmit={handleSubmitPracticeFeedPost}
        />
    </div>
  );
}
